import { anomalyTranslations } from '@/i18n/anomalyTranslations';
import { electricityPriceTranslations } from '@/i18n/electricityPriceTranslations';

export type AppLanguage = 'de' | 'en' | 'es';

export const LANGUAGE_STORAGE_KEY = 'language';

export const LANGUAGE_LOCALES: Record<AppLanguage, string> = {
    de: 'de-DE',
    en: 'en-US',
    es: 'es-ES',
};

export const LANGUAGE_LABELS: Record<AppLanguage, string> = {
    de: 'Deutsch',
    en: 'English',
    es: 'Español',
};

type TranslationMap = Record<string, string>;

type Translations = Record<AppLanguage, TranslationMap>;

export const translations: Translations = {
    de: {
        'Water loss last 12 complete months': 'Wasserverlust letzte 12 Monate',
        'Water loss previous month': 'Wasserverlust Vormonat',
        'Monthly water loss': 'Monatlicher Wasserverlust',
        'Water loss in m³': 'Wasserverlust in m³',
        'Period: {from} – {to}': 'Zeitraum: {from} – {to}',
        ...anomalyTranslations.de,
        ...electricityPriceTranslations.de,

        // General Terms
        '#chapter-1': 'General Terms',
        '1 January {year}': '1. Januar {year}',
        'All': 'Alle',
        'Automatically converted': 'Automatisch umgewandelt',
        'Available': 'Verfügbar',
        'Chart selection': 'Diagrammauswahl',
        'Code': 'Code',
        'Code type': 'Code-Typ',
        'Config': 'Konfiguration',
        'Date': 'Datum',
        'Day': 'Tag',
        'Display': 'Darstellung',
        'Direct': 'Direkt',
        'Displayed percentage source: {valueSensor}. Traffic light source: {statusSensor}.': 'Quelle des angezeigten Prozentwerts: {valueSensor}. Quelle der Ampelfarbe: {statusSensor}.',
        'Date type': 'Datumstyp',
        'Detected format:': 'Erkanntes Format:',
        'Electricity [MWh]': 'Strom [MWh]',
        'Energy loss difference (1 year)': 'Minderertrag Energiedifferenz 1 Jahr',
        'Electricity price': 'Strompreis',
        'Entries': 'Einträge',
        'Etc.': 'Etc.',
        'German': 'Deutsch',
        'Grid Feed': 'Netzeinspeisung',
        'Group by': 'Gruppieren nach',
        'Heat [MWh]': 'Wärme [MWh]',
        'Individual': 'Einzeln',
        'Information': 'Information',
        'Interval': 'Intervall',
        'Key / English': 'Schlüssel / Englisch',
        'Links': 'Links',
        'Loaded format': 'Geladenes Format',
        'License': 'Lizenz',
        'Meter': 'Zähler',
        'Month': 'Monat',
        'Monthly energy in Wh': 'Monatliche Energie in Wh',
        'Measurement': 'Messung',
        'Prediction': 'Prognose',
        'Series': 'Datenreihen',
        'Monthly': 'Monatlich',
        'Note': 'Hinweis',
        'Module 1': 'Modul 1',
        'Module 2': 'Modul 2',
        'Name': 'Name',
        'No': 'Nein',
        'No outage': 'Kein Ausfall',
        'Not available': 'Nicht vorhanden',
        'None': 'Keine',
        'Property': 'Eigenschaft',
        'Now': 'Jetzt',
        'of': 'von',
        'Placeholder': 'Platzhalter',
        'PV [MWh]': 'PV [MWh]',
        'Reference time': 'Bezugszeitpunkt',
        'Rule': 'Regel',
        'Show technical details': 'Technische Details anzeigen',
        'Spanish': 'Spanisch',
        'Storage usage': 'Speichernutzung',
        'Significant outage': 'Signifikanter Ausfall',
        'Supply type': 'Versorgungstyp',
        'Supply types': 'Versorgungstypen',
        'To': 'Bis',
        'Unknown': 'Unbekannt',
        'Minor outage': 'Kleiner Ausfall',
        'URL': 'URL',
        'URN': 'URN',
        'Used': 'Belegt',
        'Value': 'Wert',
        'Value display sensor': 'Sensor für Wertanzeige',
        'Version': 'Version',
        'Water [m³]': 'Wasser [m³]',
        'Year': 'Jahr',
        'Yearly': 'Jährlich',
        'Yes': 'Ja',

        // General Actions & Controls
        '#chapter-2': 'General Actions & Controls',
        'Add sensor': 'Sensor hinzufügen',
        'Ascending': 'Aufsteigend',
        'Clear cache': 'Cache löschen',
        'Close': 'Schließen',
        'Configuration for': 'Konfiguration für',
        'Descending': 'Absteigend',
        'Enable advanced view': 'Erweiterte Ansicht aktivieren',
        'Enable debug section': 'Debug-Bereich aktivieren',
        'Enable demo section': 'Demo-Bereich aktivieren',
        'Enable tools section': 'Werkzeug-Bereich aktivieren',
        'Enabled': 'Aktiviert',
        'Export PDF file': 'PDF exportieren',
        'Foundation': 'Grundlage',
        'Gas': 'Gas',
        'No sorting': 'Keine Sortierung',
        'Open demo': 'Demo öffnen',
        'Open info': 'Information öffnen',
        'Open price indicator': 'Preisampel öffnen',
        'Open traffic light legend': 'Ampellegende öffnen',
        'Original endpoint': 'Original-Endpunkt',
        'Original endpoints': 'Original-Endpunkte',
        'Read data': 'Daten lesen',
        'Reset': 'Zurücksetzen',
        'Reset selection and sorting': 'Auswahl und Sortierung zurücksetzen',
        'Save data': 'Daten speichern',
        'Show version': 'Zeige Version',
        'Sort': 'Sortierung',
        'View statistics': 'Statistiken anzeigen',

        // Primary Navigation & Areas
        '#chapter-3': 'Primary Navigation & Areas',
        'Account': 'Konto',
        'Advanced view': 'Erweiterte Ansicht',
        'Appearance': 'Darstellung',
        'Breadcrumb': 'Breadcrumb',
        'Cache': 'Cache',
        'Component Overview': 'Komponentenübersicht',
        'Component': 'Komponente',
        'Configuration': 'Konfiguration',
        'Configuration Demo': 'Konfiguration-Demo',
        'Contact': 'Kontakt',
        'Customer': 'Kunde',
        'Dashboard': 'Dashboard',
        'Day Ahead and forecast in the unit [€/MWh].': 'Day Ahead und Prognose in der Einheit [€/MWh].',
        'Day-Ahead Price': 'Day-Ahead-Preis',
        'Debug': 'Debug',
        'Debug Login': 'Debug-Login',
        'Debug section': 'Debug-Bereich',
        'Debugging': 'Debugging',
        'Demo': 'Demo',
        'Demo section': 'Demo-Bereich',
        'Device Definitions': 'Gerätedefinitionen',
        'Example Dashboards': 'Beispiel-Dashboards',
        'Home': 'Start',
        'Imprint': 'Impressum',
        'Language': 'Sprache',
        'Last Value': 'Letzter Wert',
        'Last Value Demo': 'Letzter Wert-Demo',
        'Layouts': 'Layouts',
        'Login': 'Login',
        'Login Demo': 'Login-Demo',
        'Logout': 'Logout',
        'Page': 'Seite',
        'Plotly': 'Plotly',
        'Plotly Demo': 'Plotly-Demo',
        'Price Traffic Light': 'Preisampel',
        'Session': 'Sitzung',
        'Statistics': 'Statistiken',
        'Statuses': 'Status',
        'System': 'System',
        'Systems': 'Systeme',
        'Thermal Conflict': 'Thermischer Konflikt',
        'Time Series': 'Zeitreihen',
        'Time Series Demo': 'Zeitreihen-Demo',
        'Typography': 'Typography',
        'Translations': 'Übersetzungen',
        'Tools': 'Werkzeuge',
        'Tools section': 'Werkzeug-Bereich',
        'Versions': 'Versionen',
        'Versions & Licenses': 'Versionen & Lizenzen',

        // Configuration Demo
        '#chapter-4': 'Configuration Demo',
        'Always sent as array by saveConfiguration().': 'Wird von saveConfiguration() immer als array gesendet.',
        'All configurations': 'Alle Konfigurationen',
        'Add note': 'Notiz hinzufügen',
        'Add pause': 'Pause hinzufügen',
        'Anomaly configurations': 'Anomaliekonfigurationen',
        'Author': 'Autor',
        'Configuration data': 'Konfigurationsdaten',
        'Configuration data is loading.': 'Konfigurationsdaten werden geladen.',
        'Configuration data is loading {completed}/{total}.': 'Konfigurationsdaten werden geladen {completed}/{total}.',
        'Configuration data could not be saved.': 'Konfigurationsdaten konnten nicht gespeichert werden.',
        'Configuration data does not match the required schema.': 'Die angegebenen Konfigurationsdaten entsprechen nicht dem erforderlichen Schema.',
        'Configuration data editor': 'Konfigurationsdaten Editor',
        'Configuration data JSON': 'Konfigurationsdaten JSON',
        'Configuration data must contain valid JSON.': 'Konfigurationsdaten müssen gültiges JSON enthalten.',
        'Configuration data was loaded.': 'Konfigurationsdaten wurden geladen.',
        'Configuration data was saved.': 'Konfigurationsdaten wurden gespeichert.',
        'Configuration request failed: {message}': 'Konfigurationsanfrage fehlgeschlagen: {message}',
        'Customer used to identify the configuration.': 'Kunde zur Identifikation der Konfiguration.',
        'Created at': 'Erstellt am',
        'Edit the configuration through form controls. Changes are synchronized with the JSON data below.': 'Konfiguration über Eingabefelder bearbeiten. Änderungen werden mit den JSON-Daten unten synchronisiert.',
        'Ends at': 'Endet am',
        'Note content': 'Notizinhalt',
        'Notes': 'Notizen',
        'No configuration data exists for the selected customer and system.': 'Für den ausgewählten Kunden und das ausgewählte System sind keine Konfigurationsdaten vorhanden.',
        'No data available.': 'Keine Daten vorhanden.',
        'Pause reason': 'Pausengrund',
        'Pauses': 'Pausen',
        'Read and save arbitrary JSON configuration data for a customer and system. Configuration reads and writes use JSON requests and are not cached.': 'Beliebige JSON-Konfigurationsdaten für einen Kunden und ein System lesen und speichern. Konfigurationsabfragen verwenden JSON und werden nicht zwischengespeichert.',
        'Read all available customer and system configurations without editing them.': 'Alle verfügbaren Kunden- und Systemkonfigurationen ohne Bearbeitungsmöglichkeit lesen.',
        'Read-only overview of all available customer and system configurations.': 'Schreibgeschützte Übersicht aller verfügbaren Kunden- und Systemkonfigurationen.',
        'Read configuration': 'Konfiguration lesen',
        'Read the saved JSON through data.data. When no configuration exists, data is null and success is false.': 'Lese das gespeicherte JSON über data.data. Wenn keine Konfiguration vorhanden ist, ist data null und success false.',
        'Read, edit, and save JSON configuration data for a customer and system.': 'JSON-Konfigurationsdaten für einen Kunden und ein System lesen, bearbeiten und speichern.',
        'Reading configuration data...': 'Konfigurationsdaten werden gelesen...',
        'Save configuration': 'Konfiguration speichern',
        'Sensor configurations': 'Sensorkonfigurationen',
        'Sensor URN': 'Sensor-URN',
        'Saving configuration data...': 'Konfigurationsdaten werden gespeichert...',
        'Settings': 'Einstellungen',
        'Starts at': 'Beginnt am',
        'System used to identify the configuration.': 'System zur Identifikation der Konfiguration.',
        'The client always sends data_type: array and stores the supplied JSON string as configuration content.': 'Der Client sendet data_type: array immer mit und speichert den übergebenen JSON-String als Konfigurationsinhalt.',
        'The tags object requires customer and system. A missing configuration returns success: false and can be handled as an empty object.': 'Das tags-Objekt benötigt customer und system. Eine fehlende Konfiguration liefert success: false und kann als leeres Objekt behandelt werden.',
        'The JSON configuration must be valid to use the editor.': 'Die JSON-Konfiguration muss gültig sein, damit der Editor verwendet werden kann.',
        'Remove': 'Entfernen',
        'Valid JSON string to save. Only required for saveConfiguration().': 'Gültiger JSON-String zum Speichern. Nur für saveConfiguration() erforderlich.',

        // Component Overview & Source Actions
        '#chapter-5': 'Component Overview & Source Actions',
        'Application files': 'Anwendungsdateien',
        'Browse the application structure and its main components.': 'Durchsuche die Anwendungsstruktur und ihre Hauptkomponenten.',
        'Browse all project translation keys and their German and Spanish values.': 'Alle Übersetzungsschlüssel des Projekts mit ihren deutschen und spanischen Werten anzeigen.',
        'Components': 'Komponenten',
        'Contexts': 'Kontexte',
        'Copied': 'Kopiert',
        'Copy code': 'Code kopieren',
        'Copy Markdown source': 'Markdown-Quellcode kopieren',
        'Copy Mermaid source': 'Mermaid-Quellcode kopieren',
        'Download diagram': 'Diagramm herunterladen',
        'Download Markdown source': 'Markdown-Quellcode herunterladen',
        'Download Mermaid source': 'Mermaid-Quellcode herunterladen',
        'Explore how application routes are connected to their React components.': 'Erkunde, wie die Anwendungsrouten mit ihren React-Komponenten verbunden sind.',
        'External components': 'Externe Komponenten',
        'Legend': 'Legende',
        'Markdown': 'Markdown',
        'MermaidDiagram': 'MermaidDiagram',
        'no project path': 'kein Projektpfad',
        'Routes': 'Routen',
        'Search pages': 'Seiten suchen',
        'StoredPlotlyPlot': 'StoredPlotlyPlot',
        'TimeSeriesPlot': 'TimeSeriesPlot',
        'TimeSeriesValue': 'TimeSeriesValue',
        'Unable to download diagram.': 'Das Diagramm konnte nicht heruntergeladen werden.',
        'URL path': 'URL-Pfad',
        'Workspace': 'Arbeitsbereich',

        // Overview, Sensors & Metrics
        '#chapter-6': 'Overview, Sensors & Metrics',
        '{count} sensor': '{count} Sensor',
        '{count} sensors': '{count} Sensoren',
        'Active sensors only': 'Nur aktivierte Sensoren',
        'Aggregation': 'Aggregation',
        'All sensors': 'Alle Sensoren',
        'Anomalies': 'Anomalien',
        'Anomaly': 'Anomalie',
        'Average': 'Durchschnitt',
        'Base Load': 'Grundlast',
        'Base Load Night': 'Grundlast Nacht',
        'Base Load Overview': 'Grundlast Übersicht',
        'Category': 'Kategorie',
        'Current': 'Aktuell',
        'Current sensor': 'Aktueller Sensor',
        'Current value': 'Aktueller Wert',
        'Current Value': 'Aktueller Wert',
        'Current Value Demo': 'Aktueller Wert-Demo',
        'Cumulative water loss {key}': 'Kumulierter Wasserverlust {key}',
        'Customers': 'Kunden',
        'Cumulative water loss in m³': 'Kumulierter Wasserverlust in m³',
        'Danger sensor count': 'Anzahl Sensoren Gefahr',
        'Date used for the Münster data link.': 'Datum für den Münster-Datenlink.',
        'Description': 'Beschreibung',
        'Detail': 'Detail',
        'Details': 'Details',
        'DTC error code': 'DTC-Fehlercode',
        'DTC value for the last day': 'DTC-Wert des letzten Tages',
        'DTC value over the last 7 days': 'DTC-Wert der letzten 7 Tage',
        'Energy sensor': 'Energiesensor',
        'Example sensor name': 'Beispiel-Sensorname',
        'Filter': 'Filter',
        'Group': 'Gruppe',
        'Group / supply type': 'Gruppe / Versorgungstyp',
        'Group sensor/device selection by': 'Auswahl Sensor / Gerät gruppieren nach',
        'Heating Energy': 'Heizenergie',
        'Heating Energy Overview': 'Heizenergie Übersicht',
        'Interval Values': 'Intervallwerte',
        'Interval Values Demo': 'Intervall Werte-Demo',
        'KPI': 'KPI',
        'KPIs': 'KPIs',
        'Link to the monitoring server': 'Link zum Monitoringserver',
        'Link to the Münster data request for date {date}': 'Link zur Münster-Datenabfrage für Datum {date}',
        'Maximum': 'Maximum',
        'Medium': 'Medium',
        'Münster data': 'Münster-Daten',
        'Münster': 'Münster',
        'Neutraubling': 'Neutraubling',
        'No sensor/device is available for the current selection.': 'Für die aktuelle Auswahl ist kein Sensor oder Gerät verfügbar.',
        'Overview': 'Übersicht',
        'Overview of all available anomaly metrics.': 'Übersicht aller verfügbaren Anomaliekennzahlen.',
        'Overview of all available base load metrics.': 'Übersicht aller verfügbaren Grundlastkennzahlen.',
        'Overview of all available customers.': 'Übersicht aller verfügbaren Kunden.',
        'Overview of all available heating energy metrics.': 'Übersicht aller verfügbaren Heizenergiekennzahlen.',
        'Overview of all available PV prediction metrics.': 'Übersicht aller verfügbaren PV-Prognosekennzahlen.',
        'Overview of all available statuses.': 'Übersicht aller verfügbaren Status.',
        'Overview of all available systems.': 'Übersicht aller verfügbaren Systeme',
        'Outdated': 'Veraltet',
        'PV Prediction': 'PV-Prognose',
        'PV Prediction Overview': 'PV-Prognose Übersicht',
        'Power sensor': 'Leistungssensor',
        'Raw response': 'Rohantwort',
        'Select a sensor/device to inspect all available sensor names and links.': 'Wählen Sie einen Sensor oder ein Gerät, um alle verfügbaren Sensornamen und Links anzuzeigen.',
        'Sensor': 'Sensor',
        'Sensor / device': 'Sensor / Gerät',
        'Sensor count': 'Anzahl Sensoren',
        'Sensor name': 'Sensorname',
        'Sensor type': 'Sensortyp',
        'Sensors': 'Sensoren',
        'Sensors / Devices': 'Sensoren / Geräte',
        'State': 'Status',
        'Status': 'Status',
        'Subsensor': 'Subsensor',
        'This is the primary sensor configured for this device.': 'Dies ist der für dieses Gerät konfigurierte Hauptsensor.',
        'Success sensor count': 'Anzahl Sensoren Erfolg',
        'Sum': 'Summe',
        'Supply type / group': 'Versorgungstyp / Gruppe',
        'Unit': 'Einheit',
        'Virtual devices': 'Virtuelle Zähler',
        'View': 'Ansicht',
        'Volume flow sensor': 'Volumenstromsensor',
        'Water loss over the last 7 days in m³/h': 'Wasserverlust der letzten 7 Tage in m³/h',
        'Water loss last 7 days': 'Wasserverlust letzte 7 Tage',
        'Warning sensor count': 'Anzahl Sensoren Warnung',

        // General Feedback & Loading
        '#chapter-7': 'General Feedback & Loading',
        'Current value was loaded.': 'Aktueller Wert wurde geladen.',
        'Current values were loaded.': 'Aktuelle Werte wurden geladen.',
        'Last value was loaded.': 'Wert wurde geladen.',
        'Last values were loaded.': 'Werte wurden geladen.',
        'Loading API request {current}/{total}': 'API-Anfrage wird geladen {current}/{total}',
        'Loading API request...': 'API-Anfrage wird geladen...',
        'Loading...': 'Lädt...',
        'No pages found.': 'Keine Seiten gefunden.',
        'Plotly JSON was loaded.': 'Plotly-JSON wurde geladen.',
        'This feature will be available soon.': 'Diese Funktion wird demnächst verfügbar sein.',
        'Time series was loaded.': 'Zeitreihen wurden geladen.',
        'Unable to load Münster data: {message}': 'Münster-Daten konnten nicht geladen werden: {message}',

        // Status & Availability
        '#chapter-8': 'Status & Availability',
        'Attention: This is a disabled sensor. The request may not return any time series data.': 'Achtung: Dies ist ein deaktivierter Sensor. Die Anfrage liefert möglicherweise keine Zeitreihendaten.',
        'Attention: This is a disabled sensor. Some sensor time series in the following table may not be available.': 'Achtung: Dies ist ein deaktivierter Sensor. Möglicherweise sind nicht alle Sensor Zeitreihen in der nachfolgenden Tabelle verfügbar.',
        'Coming soon': 'Erscheint bald',
        'Disabled sensor': 'Deaktivierter Sensor',
        'Displayed value was converted.': 'Der angezeigte Wert wurde umgerechnet.',
        'Critical': 'Kritisch',
        'Error': 'Fehler',
        'Expensive': 'Teuer',
        'Favourable': 'Günstig',
        'General Error': 'Genereller Fehler',
        'Implausible': 'Unplausibel',
        'Inactive': 'Inaktiv',
        'No sensor or sensors available': 'Kein Sensor oder Sensoren vorhanden',
        'Normal': 'Normal',
        'Not implemented': 'Nicht implementiert',
        'Not implemented yet.': 'Noch nicht implementiert.',
        'Not monitored': 'Nicht überwacht',
        'Not supported': 'Nicht unterstützt',
        'OK': 'OK',
        'Otherwise': 'Andernfalls',
        'Performance Deviation': 'Abweichende Leistung',
        'Sensor enabled': 'Sensor aktiviert',
        'Sensor not available or not enabled': 'Sensor nicht vorhanden oder nicht aktiviert',
        'Sensor not enabled': 'Sensor nicht aktiviert',
        'Source values': 'Quellwerte',
        'Status calculation': 'Statusberechnung',
        'Traffic light legend': 'Ampellegende',
        'Warning': 'Warnung',

        // Session & Authentication
        '#chapter-9': 'Session & Authentication',
        'API token acquired successfully.': 'API-Token erfolgreich erhalten.',
        'API token check completed successfully. ({durationMs} ms)': 'API-Token-Prüfung erfolgreich abgeschlossen. ({durationMs} ms)',
        'API token check failed. Session was cleared.': 'API-Token-Prüfung fehlgeschlagen. Die Sitzung wurde gelöscht.',
        'API token check failed: {message}': 'API-Token-Prüfung fehlgeschlagen: {message}',
        'API token check succeeded. Session appears valid.': 'API-Token-Prüfung erfolgreich. Die Sitzung scheint gültig zu sein.',
        'API token likely expired': 'API-Token vermutlich abgelaufen',
        'Check API token': 'API-Token prüfen',
        'Check API token output': 'Ausgabe der API-Token-Prüfung',
        'Check, renew, or clear the current API token.': 'Prüfe, erneuere oder lösche das aktuelle API-Token.',
        'Checking API token...': 'API-Token wird geprüft...',
        'Current API token': 'Aktuelles API-Token',
        'Failed to renew API token: {message}': 'API-Token konnte nicht erneuert werden: {message}',
        'Go to Login': 'Zum Login wechseln',
        'Hide password': 'Passwort ausblenden',
        'Imported at {date}': 'Übernommen am {date}',
        'Invalid username or password': 'Ungültiger Benutzername oder Passwort.',
        'Logged in': 'Eingeloggt',
        'Logged in as': 'Eingeloggt als',
        'Logged out': 'Ausgeloggt',
        'Login failed: {message}': 'Login fehlgeschlagen: {message}',
        'No API request executed yet.': 'Noch keine API-Anfrage ausgeführt.',
        'No API token available.': 'Kein API-Token vorhanden.',
        'Password': 'Passwort',
        'Refreshing API token...': 'API-Token wird erneuert...',
        'Refreshing session...': 'Sitzung wird erneuert...',
        'Renew Login': 'Login erneuern',
        'Renew API token': 'API-Token erneuern',
        'Session status': 'Sitzungsstatus',
        'Show password': 'Passwort anzeigen',
        'Sign in': 'Anmelden',
        'Sign in to continue': 'Zum Fortfahren anmelden',
        'Signing in...': 'Anmeldung läuft...',
        'The API token check failed because the current API token appears to be expired. Renew the login and try again.': 'Die API-Token-Prüfung ist fehlgeschlagen, weil das aktuelle API-Token offenbar abgelaufen ist. Erneuern Sie das Login und versuchen Sie es erneut.',
        'The API token check response will be shown here.': 'Die Antwort der API-Token-Prüfung wird hier angezeigt.',
        'The current API token appears to be no longer valid and should be renewed.': 'Das aktuelle API-Token scheint nicht mehr gültig zu sein und sollte erneuert werden.',
        'Toggle light and dark theme': 'Helles und dunkles Theme umschalten',
        'Unknown login error.': 'Unbekannter Login-Fehler.',
        'Username': 'Benutzername',
        'Using API token restored from session storage.': 'API-Token aus dem Session Storage wiederhergestellt.',

        // Requests & Generic Errors
        '#chapter-10': 'Requests & Generic Errors',
        'API request failed: {message}': 'API-Anfrage fehlgeschlagen: {message}',
        'Current value request failed.': 'Anfrage des aktuellen Werts fehlgeschlagen.',
        'Current value response is not a CSV string.': 'Die Antwort des aktuellen Werts ist kein CSV-String.',
        'Last value request failed.': 'Letzter-Wert-Anfrage fehlgeschlagen.',
        'Last value response is not a CSV string.': 'Die Letzter-Wert-Antwort ist kein CSV-String.',
        'Missing current value request parameters.': 'Fehlende Anfrageparameter für den aktuellen Wert.',
        'Missing last value request parameters.': 'Fehlende Letzter-Wert-Anfrageparameter.',
        'Missing Plotly request parameters.': 'Fehlende Plotly-Anfrageparameter.',
        'Missing time series request parameters.': 'Fehlende Zeitreihen-Anfrageparameter.',
        'No interval values were found for sensors {sensors}.': 'Für die Sensoren {sensors} wurden keine Intervallwerte gefunden.',
        'Plotly request failed.': 'Plotly-Anfrage fehlgeschlagen.',
        'Request status': 'Anfragestatus',
        'Requesting example dataset...': 'Beispieldatensatz wird angefragt...',
        'Requesting last value dataset...': 'Letzter-Wert-Datensatz wird angefragt...',
        'Requesting last value...': 'Letzter Wert wird angefragt...',
        'Requesting last values...': 'Letzte Werte werden angefragt...',
        'Requesting Plotly JSON...': 'Plotly-JSON wird angefragt...',
        'Requesting time series dataset...': 'Zeitreihendaten werden angefragt...',
        'Requesting time series...': 'Zeitreihen werden angefragt...',
        'Sensor {sensor} returned no numeric current value.': 'Sensor {sensor} hat keinen numerischen aktuellen Wert geliefert.',
        'Sensor {sensor} was not found.': 'Sensor {sensor} wurde nicht gefunden.',
        'The Plotly request failed because the current API token appears to be expired. Renew the login and try again.': 'Die Plotly-Anfrage ist fehlgeschlagen, weil das aktuelle API-Token offenbar abgelaufen ist. Erneuern Sie das Login und versuchen Sie es erneut.',
        'The time series request failed because the current API token appears to be expired. Renew the login and try again.': 'Die Zeitreihenanfrage ist fehlgeschlagen, weil das aktuelle API-Token offenbar abgelaufen ist. Erneuern Sie das Login und versuchen Sie es erneut.',
        'Time series request failed.': 'Zeitreihenanfrage fehlgeschlagen.',
        'Time series response is not a CSV string.': 'Die Zeitreihenantwort ist kein CSV-String.',
        'Unknown request error.': 'Unbekannter Anfragefehler.',

        // Page Descriptions & Navigation
        '#chapter-11': 'Page Descriptions & Navigation',
        '{anomalyCount} anomalies from {sensorCount} sensor found: {details}': '{anomalyCount} Anomalien von {sensorCount} Sensor gefunden: {details}',
        '{anomalyCount} anomalies from {sensorCount} sensors found: {details}': '{anomalyCount} Anomalien von {sensorCount} Sensoren gefunden: {details}',
        '{anomalyCount} anomaly from {sensorCount} sensor found: {details}': '{anomalyCount} Anomalie von {sensorCount} Sensor gefunden: {details}',
        '{anomalyCount} anomaly from {sensorCount} sensors found: {details}': '{anomalyCount} Anomalie von {sensorCount} Sensoren gefunden: {details}',
        '{sensorCount} {anomaly} sensor': '{sensorCount} {anomaly} Sensor',
        '{sensorCount} {anomaly} sensors': '{sensorCount} {anomaly} Sensoren',
        'Base Load {key}': 'Grundlast {key}',
        'Base load anomaly monitoring for energy-cost deviations and baseline behavior.': 'Überwachung von Grundlastanomalien für Energiekostenabweichungen und das Grundlastverhalten.',
        'Base load dashboard': 'Grundlast-Dashboard',
        'Heating Energy {key}': 'Heizenergie {key}',
        'Heating energy anomaly monitoring for temperatures, reductions, and saved energy.': 'Überwachung von Heizenergieanomalien für Temperaturen, Absenkungen und eingesparte Energie.',
        'Heating energy dashboard': 'Heizenergie-Dashboard',
        'Home navigation': 'Startnavigation',
        'Metrics from customer {customer}.': 'Kennzahlen des Kunden {customer}.',
        'Metrics from system {system}.': 'Kennzahlen vom System {system}.',
        'Metrics with {status} status.': 'Kennzahlen mit Status {status}.',
        'No anomalies found for {sensorCount} sensor.': 'Keine Anomalien von {sensorCount} Sensor gefunden.',
        'No anomalies found for {sensorCount} sensors.': 'Keine Anomalien von {sensorCount} Sensoren gefunden.',
        'Open base load dashboard for {key}': 'Grundlast-Dashboard für {key} öffnen',
        'Overview dashboard': 'Übersichts-Dashboard',
        'Photovoltaic': 'Photovoltaik',
        'PV prediction {key}': 'PV-Prognose {key}',
        'PV Prediction {key}': 'PV-Prognose {key}',
        'PV prediction anomaly monitoring for feed-in performance, forecasting quality, and operational warnings.': 'Überwachung von PV-Prognoseanomalien für Einspeiseleistung, Prognosequalität und betriebliche Warnungen.',
        'PV prediction dashboard': 'PV-Prognose-Dashboard',
        'Select a system to open its metrics:': 'Wählen Sie ein System, um die zugehörigen Kennzahlen zu öffnen:',
        'Select an anomaly to open its dashboard and related metrics:': 'Wählen Sie eine Anomalie, um das zugehörige Dashboard und die Kennzahlen zu öffnen:',
        'Statistics dashboard': 'Statistik-Dashboard',
        'Statistics for this system are currently being prepared.': 'Die Statistiken für dieses System befinden sich aktuell im Aufbau.',
        'Statistics overview for system {key}.': 'Statistikübersicht für das System {key}.',
        'System details for {system} will be available soon.': 'Details zum System {system} erscheinen bald.',
        'The day-ahead price shows the wholesale electricity price for the Germany/Luxembourg market area.': 'Der Day-Ahead-Preis zeigt den Stromgroßhandelspreis für die Gebotszone Deutschland/Luxemburg.',
        'Thermal conflict dashboard': 'Dashboard thermischer Konflikt',
        'Thermal conflict monitoring for thermal conditions and potential conflicts.': 'Überwachung thermischer Konflikte für thermische Zustände und mögliche Konflikte.',

        // Layouts, Typography & Demo Content
        '#chapter-12': 'Layouts, Typography & Demo Content',
        'API': 'API',
        'API2 Data': 'API2-Daten',
        'Api2LastValueData and Api2TimeSeriesData provide resolved API data for custom rendering.': 'Api2LastValueData und Api2TimeSeriesData stellen aufgelöste API-Daten für individuelle Ausgaben bereit.',
        'Browse example dashboard layouts and their responsive behavior.': 'Durchsuche Beispiel-Dashboard-Layouts und ihr responsives Verhalten.',
        'Browse UI layout components and implementation examples.': 'Durchsuche UI-Layout-Komponenten und Implementierungsbeispiele.',
        'Selected reusable components with implementation examples and rendered previews.': 'Ausgewählte wiederverwendbare Komponenten mit Implementierungsbeispielen und gerenderten Vorschauen.',
        'Call example': 'Aufrufbeispiel',
        'Charts, diagrams and structured content components.': 'Komponenten für Diagramme, Visualisierungen und strukturierte Inhalte.',
        'Configured reference of the stored Plotly figure.': 'Konfigurierte Referenz der gespeicherten Plotly-Figur.',
        'Acquires or renews the API bearer token used by all authenticated requests.': 'Ruft das API-Bearer-Token für alle authentifizierten Anfragen ab oder erneuert es.',
        'Current API bearer token from useSession().': 'Aktuelles API-Bearer-Token aus useSession().',
        'Current values': 'Aktuelle Werte',
        'Dashboard layout primitives and typographic defaults.': 'Grundbausteine für Dashboard-Layouts und typografische Standardwerte.',
        'Data': 'Daten',
        'Description.': 'Beschreibung.',
        'End time, for example NOW or a formatted date-time value.': 'Endzeit, zum Beispiel NOW oder ein formatiertes Datum mit Uhrzeit.',
        'Every API operation returns data together with request metadata. The client selects the appropriate transport automatically for developer and live builds.': 'Jede API-Operation gibt Daten zusammen mit Anfrage-Metadaten zurück. Der Client wählt den passenden Transport für Developer- und Live-Builds automatisch aus.',
        'Example dashboard for visualizing thermal conflicts and related metrics.': 'Beispiel-Dashboard zur Visualisierung thermischer Konflikte und zugehöriger Kennzahlen.',
        'Example overview dashboard with combined KPI cards and responsive grid behavior.': 'Beispiel-Übersichts-Dashboard mit kombinierten KPI-Karten und responsivem Grid-Verhalten.',
        'Example response': 'Beispielantwort',
        'Example statistics dashboard with chart-focused panels and responsive grid behavior.': 'Beispiel-Statistik-Dashboard mit diagrammfokussierten Panels und responsivem Grid-Verhalten.',
        'Interval values': 'Intervallwerte',
        'KPI API2 Current Value': 'KPI API2 Aktueller Wert',
        'KPI API2 Last Value': 'KPI API2 Letzter Wert',
        'KPI API2 Time Series': 'KPI API2 Zeitreihen',
        'KPI cards and API data sources for monitoring values.': 'KPI-Karten und API-Datenquellen für Monitoring-Werte.',
        'KPI Static': 'Statische KPIs',
        'KPIs & API Data': 'KPIs & API-Daten',
        'KpiValueApi2LastValue and KpiCarouselApi2LastValue load current API2 values directly.': 'KpiValueApi2LastValue und KpiCarouselApi2LastValue laden aktuelle API2-Werte direkt.',
        'KpiValueApi2TimeSeries and KpiCarouselApi2TimeSeries load and aggregate API2 time series.': 'KpiValueApi2TimeSeries und KpiCarouselApi2TimeSeries laden und aggregieren API2-Zeitreihen.',
        'KpiValueStatic and KpiCarouselStatic render manually provided KPI values.': 'KpiValueStatic und KpiCarouselStatic stellen manuell übergebene KPI-Werte dar.',
        'Last values': 'Letzte Werte',
        'Layout & Foundations': 'Layout & Grundlagen',
        'Learn how to call the central EE-Check API client from project code.': 'Erfahre, wie der zentrale EE-Check-API-Client aus Projektcode aufgerufen wird.',
        'List item 1': 'Listenpunkt 1',
        'List item 2': 'Listenpunkt 2',
        'Loads a saved Plotly figure. The returned data can be passed directly to the shared Plotly components.': 'Lädt eine gespeicherte Plotly-Figur. Die zurückgegebenen Daten können direkt an die gemeinsamen Plotly-Komponenten übergeben werden.',
        'Loads CSV time series data for one or more comma-separated URNs. Large URN lists are split and merged automatically.': 'Lädt CSV-Zeitreihendaten für eine oder mehrere kommagetrennte URNs. Große URN-Listen werden automatisch aufgeteilt und zusammengeführt.',
        'Loads current values and passes resolved data into a render function without imposing a KPI layout.': 'Lädt aktuelle Werte und übergibt aufgelöste Daten an eine Render-Funktion, ohne ein KPI-Layout vorzugeben.',
        'Loads interval differences for one or more comma-separated URNs between two required timestamps.': 'Lädt Intervall-Differenzen für eine oder mehrere kommagetrennte URNs zwischen zwei erforderlichen Zeitpunkten.',
        'Loads the current value, or the last value before an optional timestamp, for one or more comma-separated URNs.': 'Lädt den aktuellen Wert oder den letzten Wert vor einem optionalen Zeitpunkt für eine oder mehrere kommagetrennte URNs.',
        'Loads the latest value, or the last value before an optional timestamp, for one or more comma-separated URNs.': 'Lädt den neuesten Wert oder den letzten Wert vor einem optionalen Zeitpunkt für eine oder mehrere kommagetrennte URNs.',
        'Login and token renewal': 'Login und Token-Erneuerung',
        'Markdown renders lightweight formatted content such as headings, tables, and explanatory notes.': 'Markdown rendert leichtgewichtige formatierte Inhalte wie Überschriften, Tabellen und erläuternde Hinweise.',
        'MermaidDiagram renders text-based diagrams such as flowcharts, sequence diagrams, and class diagrams.': 'MermaidDiagram rendert textbasierte Diagramme wie Flussdiagramme, Sequenzdiagramme und Klassendiagramme.',
        'One URN or multiple comma-separated URNs.': 'Eine URN oder mehrere kommagetrennte URNs.',
        'One URN or multiple comma-separated URNs. EE-Check function URNs are encoded by the client.': 'Eine URN oder mehrere kommagetrennte URNs. EE-Check-Funktions-URNs werden durch den Client kodiert.',
        'Optional interval. The default is 1year; another example is 1month.': 'Optionales Intervall. Der Standard ist 1year; ein weiteres Beispiel ist 1month.',
        'Optional layout overrides for the stored figure.': 'Optionale Layout-Überschreibungen für die gespeicherte Figur.',
        'Optional object with username and password. Without it, configured default credentials are used.': 'Optionales Objekt mit Benutzername und Passwort. Ohne dieses werden konfigurierte Standardzugangsdaten verwendet.',
        'Optional progress callback with currentRequest and totalRequests.': 'Optionaler Fortschritts-Callback mit currentRequest und totalRequests.',
        'Optional progress callback, especially useful when a large URN list is split.': 'Optionaler Fortschritts-Callback, besonders hilfreich bei der Aufteilung großer URN-Listen.',
        'Optional reference time. Omit it for the current last value; examples include NOW and 2026-01-01 00:00:00.': 'Optionaler Bezugszeitpunkt. Für den aktuellen letzten Wert weglassen; Beispiele sind NOW und 2026-01-01 00:00:00.',
        'Optional reference time. Omit it for the current value; examples include NOW and 2026-01-01 00:00:00.': 'Optionaler Bezugszeitpunkt. Für den aktuellen Wert weglassen; Beispiele sind NOW und 2026-01-01 00:00:00.',
        'Parameter': 'Parameter',
        'Reference for calling the central EE-Check API client from project components.': 'Referenz zum Aufrufen des zentralen EE-Check-API-Clients aus Projektkomponenten.',
        'Required': 'Erforderlich',
        'Required end time, for example NOW or a formatted date-time value.': 'Erforderliche Endzeit, zum Beispiel NOW oder ein formatiertes Datum mit Uhrzeit.',
        'Required start time, for example 2018-01-01.': 'Erforderliche Startzeit, zum Beispiel 2018-01-01.',
        'Select a customer and group filter to generate virtual device definitions.': 'Wählen Sie einen Kunden und Gruppenfilter, um virtuelle Zählerdefinitionen zu erzeugen.',
        'Select a demo to explore the available examples and reference pages:': 'Wähle eine Demo, um die verfügbaren Beispiele und Referenzseiten zu öffnen:',
        'Select a tool to explore the available reference pages and utilities:': 'Wähle ein Werkzeug, um die verfügbaren Referenzseiten und Hilfsmittel zu öffnen:',
        'Select an example dashboard to open its responsive dashboard layout:': 'Wählen Sie ein Beispiel-Dashboard aus, um dessen responsives Dashboard-Layout zu öffnen:',
        'Select one layout building block to open its examples and implementation details:': 'Wählen Sie einen Layout-Baustein aus, um Beispiele und Implementierungsdetails zu öffnen:',
        'Shared response format': 'Gemeinsames Antwortformat',
        'Start time, for example NOW-4WEEKS or a formatted date-time value.': 'Startzeit, zum Beispiel NOW-4WEEKS oder ein formatiertes Datum mit Uhrzeit.',
        'Store data.token and pass it to all authenticated data requests.': 'Speichere data.token und übergib es an alle authentifizierten Datenanfragen.',
        'Stored Plotly data': 'Gespeicherte Plotly-Daten',
        'Fixed query parameter added automatically by the client.': 'Fester Query-Parameter, der automatisch vom Client ergänzt wird.',
        'Loads a stored chart definition by name. Chart.js responses are converted before rendering, while Plotly responses are used directly.': 'Lädt eine gespeicherte Chart-Definition anhand ihres Namens. Chart.js-Antworten werden vor der Darstellung konvertiert, Plotly-Antworten direkt verwendet.',
        'Name of the stored chart definition to load.': 'Name der zu ladenden gespeicherten Chart-Definition.',
        'The data field contains Chart.js or Plotly JSON. Convert only Chart.js configurations with ChartJsToPlotlyConverter before using shared Plotly components.': 'Das Datenfeld enthält Chart.js- oder Plotly-JSON. Wandle nur Chart.js-Konfigurationen mit ChartJsToPlotlyConverter um, bevor du gemeinsame Plotly-Komponenten verwendest.',
        'Use this for server-stored chart definitions that should be rendered with the shared Plotly chart components.': 'Verwende dies für serverseitig gespeicherte Chart-Definitionen, die mit den gemeinsamen Plotly-Chart-Komponenten dargestellt werden sollen.',
        'StoredPlotlyPlot renders ready-made Plotly JSON figures from static input or API requests.': 'StoredPlotlyPlot rendert fertige Plotly-JSON-Figuren aus statischen Eingaben oder API-Anfragen.',
        'Tags used to resolve the requested figure, for example meter.': 'Tags zur Auflösung der angefragten Figur, zum Beispiel meter.',
        'The data field contains CSV text with timestamp, URN, value, and unit. Parse it with parseLastValueCsv().': 'Das Datenfeld enthält CSV-Text mit Zeitstempel, URN, Wert und Einheit. Werte ihn mit parseLastValueCsv() aus.',
        'The data field contains CSV text. Parse it with parseTimeSeriesCsv() before rendering or aggregating values.': 'Das Datenfeld enthält CSV-Text. Werte ihn vor Darstellung oder Aggregation mit parseTimeSeriesCsv() aus.',
        'The data field contains CSV text. parseIntervalValueCsv() extracts timestamps and sensor columns together with their units.': 'Das Datenfeld enthält CSV-Text. parseIntervalValueCsv() extrahiert Zeitstempel und Sensorspalten zusammen mit ihren Einheiten.',
        'TimeSeriesPlot renders parsed time series data as shared Plotly figures from static or API data.': 'TimeSeriesPlot rendert geparste Zeitreihendaten als gemeinsame Plotly-Figuren aus statischen oder API-Daten.',
        'TimeSeriesValue renders KPI-style values from static input or aggregated API time series.': 'TimeSeriesValue rendert KPI-artige Werte aus statischen Eingaben oder aggregierten API-Zeitreihen.',
        'Type': 'Typ',
        'Typography shows the base tag styling for headings and paragraphs from the shared stylesheet layer.': 'Typography zeigt das Basis-Tag-Styling für Überschriften und Absätze aus der gemeinsamen Stylesheet-Schicht.',
        'Use case': 'Anwendungsfall',
        'Use data.data, data.layout, and data.config as Plotly figure properties.': 'Verwende data.data, data.layout und data.config als Eigenschaften der Plotly-Figur.',
        'Use this for current KPI values and lightweight overview data without loading a full time series.': 'Verwende dies für aktuelle KPI-Werte und schlanke Übersichtsdaten ohne das Laden vollständiger Zeitreihen.',
        'Use this for interval-based consumption values without calculating differences from meter readings in the application.': 'Verwende dies für intervallbasierte Verbrauchswerte, ohne Differenzen aus Zählerständen in der Anwendung zu berechnen.',
        'Use this for plots, aggregations, and KPI values based on a period of measurements.': 'Verwende dies für Plots, Aggregationen und KPI-Werte auf Basis eines Messzeitraums.',
        'Use this for saved server-side Plotly charts, for example through Api2StoredPlotlyPlot.': 'Verwende dies für gespeicherte serverseitige Plotly-Charts, zum Beispiel über Api2StoredPlotlyPlot.',
        'Use this for the login form and for renewing an expired API token.': 'Verwende dies für das Login-Formular und zum Erneuern eines abgelaufenen API-Tokens.',
        'Visualization & Content': 'Visualisierung & Inhalte',

        // Filters, Selection & Form Labels
        '#chapter-13': 'Filters, Selection & Form Labels',
        'Custom date': 'Benutzerdefiniertes Datum',
        'From': 'Von',
        'Group filter': 'Gruppenfilter',
        'No sensors selected yet.': 'Noch keine Sensoren ausgewählt.',
        'No subsensor': 'Kein Subsensor',
        'Position:': 'Position:',
        'Range': 'Zeitraum',
        'Reference': 'Referenz',
        'Remove sensor': 'Sensor entfernen',
        'Selected group': 'Gewählte Gruppe',
        'Selected sensor': 'Gewählter Sensor',
        'Selected sensors': 'Gewählte Sensoren',
        'Sensor is already in list.': 'Sensor ist in Liste schon vorhanden.',
        'To Date': 'Bis-Datum',
        'Without group': 'Ohne Gruppe',

        // Plotly
        '#chapter-14': 'Plotly',
        '{meter} using {reference} with {range}': '{meter} mit {reference} und {range}',
        'Loading Plotly chart...': 'Plotly-Chart wird geladen...',
        'No Plotly chart available.': 'Kein Plotly-Chart verfügbar.',
        'No Plotly chart loaded yet.': 'Noch kein Plotly-Chart geladen.',
        'Plotly chart output': 'Plotly-Chart-Ausgabe',
        'Plotly JSON request completed successfully. ({durationMs} ms)': 'Plotly-JSON-Anfrage erfolgreich abgeschlossen. ({durationMs} ms)',
        'Plotly request target': 'Plotly-Anfrageziel',
        'API request payload': 'API-Anfrage (schreibgeschützt)',
        'Request and render interactive Plotly chart data.': 'Frage interaktive Plotly-Chartdaten ab und stelle sie dar.',
        'Chart Definition': 'Chart-Definition',
        'Chart Definitions': 'Chart Definitionen',
        'Chart.js JSON': 'Chart.js-JSON',
        'Chart definition request failed: {message}': 'Anfrage der Chart-Definition fehlgeschlagen: {message}',
        'Request stored chart definitions and render them as interactive Plotly charts.': 'Frage gespeicherte Chart-Definitionen ab und stelle sie als interaktive Plotly-Charts dar.',
        'Request a stored chart definition and render it as an interactive Plotly chart.': 'Frage eine gespeicherte Chart-Definition ab und stelle sie als interaktiven Plotly-Chart dar.',
        'Request chart definition': 'Chart-Definition anfragen',
        'Requesting chart definition...': 'Chart-Definition wird angefragt...',
        'The API response does not contain a valid chart definition.': 'Die API-Antwort enthält keine gültige Chart-Definition.',
        'Request Plotly JSON': 'Plotly-JSON anfragen',
        'Building statistics': 'Gebäudestatistiken',
        'Building statistics could not be loaded.': 'Gebäudestatistiken konnten nicht geladen werden.',
        'Building statistics request failed: {message}': 'Anfrage der Gebäudestatistiken fehlgeschlagen: {message}',
        'Building statistics were loaded.': 'Gebäudestatistiken wurden geladen.',
        'Building system URN, for example Mue.0531 or GN.Building.': 'System-URN des Gebäudes, zum Beispiel Mue.0531 oder GN.Building.',
        'Each supply type is either an object with four aligned value and timestamp series or an empty array when no data is available.': 'Jeder Versorgungstyp ist entweder ein Objekt mit vier aufeinander abgestimmten Wert- und Zeitstempelreihen oder ein leeres Array, wenn keine Daten verfügbar sind.',
        'First value': 'Erster Wert',
        'Grid feed': 'Netzeinspeisung',
        'Last value': 'Letzter Wert',
        'No building statistics loaded yet.': 'Noch keine Gebäudestatistiken geladen.',
        'No data is available for this supply type.': 'Für diesen Versorgungstyp sind keine Daten verfügbar.',
        'Number of timestamps / values': 'Anzahl Zeitstempel / Werte',
        'Request building statistics': 'Gebäudestatistiken anfragen',
        'Requesting building statistics...': 'Gebäudestatistiken werden angefragt...',
        'Load summarized energy statistics for every available supply type of a system.': 'Lade zusammengefasste Energiestatistiken für alle verfügbaren Versorgungstypen eines Systems.',
        'Loads daily, monthly, and yearly energy statistics for every available supply type of a building.': 'Lädt tägliche, monatliche und jährliche Energiestatistiken für alle verfügbaren Versorgungstypen eines Gebäudes.',
        'Use this for compact building consumption summaries without requesting and calculating every energy interval separately.': 'Verwende dies für kompakte Verbrauchsübersichten eines Gebäudes, ohne jedes Energieintervall einzeln abzufragen und zu berechnen.',

        // Time Series & Last Values
        '#chapter-15': 'Time Series & Last Values',
        '{from} to {to}': '{from} bis {to}',
        'Chart type': 'Diagrammtyp',
        'Current cost value {key}': 'Aktueller Kostenwert {key}',
        'Current value API request completed successfully. ({durationMs} ms)': 'API-Anfrage des aktuellen Werts erfolgreich abgeschlossen. ({durationMs} ms)',
        'Current value output': 'Ausgabe des aktuellen Werts',
        'Current value target': 'Ziel für den aktuellen Wert',
        'Grouped Bar Chart': 'Gruppiertes Balkendiagramm',
        'Interval value API request completed successfully. ({durationMs} ms)': 'Intervallwert-API-Anfrage erfolgreich abgeschlossen. ({durationMs} ms)',
        'Interval value output': 'Ausgabe der Intervallwerte',
        'Interval value target': 'Ziel für die Intervallwerte',
        'Interval values were loaded.': 'Intervallwerte wurden geladen.',
        'Last value API request completed successfully. ({durationMs} ms)': 'Letzter-Wert-API-Anfrage erfolgreich abgeschlossen. ({durationMs} ms)',
        'Last value output': 'Letzter-Wert-Ausgabe',
        'Last value target': 'Letzter-Wert-Ziel',
        'Last value timestamp': 'Letzter-Wert-Zeitpunkt',
        'Line Chart': 'Liniendiagramm',
        'Loading time series chart...': 'Zeitreihen-Chart wird geladen...',
        'No time series chart available.': 'Kein Zeitreihen-Chart verfügbar.',
        'No time series values were found for sensor {sensor}.': 'Für Sensor {sensor} wurden keine Zeitreihenwerte gefunden.',
        'No valid time series chart data available.': 'Keine gültigen Zeitreihen-Chartdaten verfügbar.',
        'Parsed columns': 'Geparste Spalten',
        'Parsed rows': 'Geparste Zeilen',
        'Period': 'Zeitraum',
        'Power in kWh': 'Leistung in kWh',
        'Power in kW': 'Leistung in kW',
        'PV power in kW': 'PV-Leistung in kW',
        'Power history (3 months)': 'Leistungsverlauf (3 Mon.)',
        'Power history (12 months)': 'Leistungsverlauf (12 Mon.)',
        'Power history {key}': 'Leistungsverlauf (15-Minuten-Werte) {key}',
        'Heating power history (3 months)': 'Wärmeleistungsverlauf (3 Mon.)',
        'Heating power history (12 months)': 'Wärmeleistungsverlauf (12 Mon.)',
        'Heating power history {key}': 'Wärmeleistungsverlauf {key}',
        'Heating power in kW': 'Wärmeleistung in kW',
        'Preview rows shown': 'Angezeigte Vorschauzeilen',
        'Request and inspect time series data.': 'Frage Zeitreihendaten ab und untersuche sie.',
        'Request Current Value': 'Aktuellen Wert anfragen',
        'Request Current Values': 'Aktuelle Werte anfragen',
        'Request interval differences for one or more sensors.': 'Intervall-Differenzen für einen oder mehrere Sensoren anfragen.',
        'Request Interval Values': 'Intervallwerte anfragen',
        'Request Last Value': 'Letzten Wert anfragen',
        'Request Last Values': 'Letzte Werte anfragen',
        'Request the latest value for one or more sensors.': 'Frage den letzten Wert für einen oder mehrere Sensoren ab.',
        'Request Time Series': 'Zeitreihen anfragen',
        'Requesting current value dataset...': 'Datensatz des aktuellen Werts wird angefragt...',
        'Requesting current value...': 'Aktueller Wert wird angefragt...',
        'Requesting current values...': 'Aktuelle Werte werden angefragt...',
        'Requesting interval value dataset...': 'Datensatz der Intervallwerte wird angefragt...',
        'Requesting interval values...': 'Intervallwerte werden angefragt...',
        'Showing only the first {limit} parsed rows to keep browser rendering lightweight.': 'Es werden nur die ersten {limit} geparsten Zeilen angezeigt, damit die Browser-Darstellung leichtgewichtig bleibt.',
        'Stacked Bar Chart': 'Gestapeltes Balkendiagramm',
        'The calculation code is not available for this calculated value.': 'Für diesen berechneten Wert ist kein calcCode hinterlegt.',
        'The current value request failed because the current API token appears to be expired. Renew the login and try again.': 'Die Anfrage des aktuellen Werts ist fehlgeschlagen, weil das aktuelle API-Token offenbar abgelaufen ist. Erneuern Sie das Login und versuchen Sie es erneut.',
        'The displayed and classified value was calculated with "calc" as follows:': 'Der angezeigte und eingeordnete Wert wurde wie folgt mit "calc" berechnet:',
        'The displayed value was calculated with calc.': 'Der angezeigte Wert wurde mit calc berechnet.',
        'The interval value request failed because the current API token appears to be expired. Renew the login and try again.': 'Die Intervallwert-Anfrage ist fehlgeschlagen, weil das aktuelle API-Token offenbar abgelaufen ist. Erneuern Sie das Login und versuchen Sie es erneut.',
        'The raw current value response will be shown here.': 'Die Rohantwort des aktuellen Werts wird hier angezeigt.',
        'The raw interval value response will be shown here.': 'Die Rohantwort der Intervallwerte wird hier angezeigt.',
        'The raw last value response will be shown here.': 'Die rohe Letzter-Wert-Antwort wird hier angezeigt.',
        'The raw time series response will be shown here.': 'Die rohe Zeitreihenantwort wird hier angezeigt.',
        'Time interval': 'Zeitintervall',
        'Time series API request completed successfully. ({durationMs} ms)': 'Zeitreihen-API-Anfrage erfolgreich abgeschlossen. ({durationMs} ms)',
        'Time series chart output': 'Zeitreihen-Chart-Ausgabe',
        'Time series group': 'Zeitreihengruppe',
        'Time series output': 'Zeitreihen-Ausgabe',
        'Time series range': 'Zeitreihenzeitraum',
        'Time series target': 'Zeitreihenziel',
        'Time series values for sensor {sensor} are not numeric.': 'Die Zeitreihenwerte für Sensor {sensor} sind nicht numerisch.',

        // Overview Results
        '#chapter-16': 'Overview Results',
        'No electricity consumption sensor was found for this system.': 'Für dieses System wurde kein Stromverbrauchssensor gefunden.',
        'No sensors were found for the current selection.': 'Für die aktuelle Auswahl wurden keine Sensoren gefunden.',

        // System Statistics
        '#chapter-17': 'System Statistics',
        'Annual electricity, heat, water consumption and photovoltaic generation calculated from meter reading differences for system {key}.': 'Jährliche Strom-, Wärme- und Wasserverbräuche sowie PV-Erzeugung, berechnet aus Zählerstandsdifferenzen für System {key}.',

        // KPI, Metrics & Business Labels
        '#chapter-18': 'KPI, Metrics & Business Labels',
        'Base load prediction': 'Grundlast-Prognose',
        'Base load prediction (14d)': 'Grundlast-Prognose (14d)',
        'Base load prediction (all)': 'Grundlast-Prognose (all)',
        'Base load prediction {key}': 'Grundlast-Prognose {key}',
        'Browse all available KPI definitions and example sensor names.': 'Durchsuche alle verfügbaren KPI-Definitionen und Beispiel-Sensornamen.',
        'Cost last 14 days': 'Kosten der letzten 14 Tage',
        'Cost last 14 days {key}': 'Kosten der letzten 14 Tage {key}',
        'Cost last month': 'Kosten letzten Monat',
        'Cost last value {key}': 'Letzter Kostenwert {key}',
        'Cost last year': 'Kosten letztes Jahr',
        'Cost overall': 'Gesamtkosten',
        'Cost overall {key}': 'Gesamtkosten {key}',
        'Comparison value from {date}': 'Vergleichswert vom {date}',
        'Cutoff temperature': 'Grenztemperatur',
        'Cutoff temperature {key}': 'Grenztemperatur {key}',
        'Current cost value': 'Aktueller Kostenwert',
        'Example KPI': 'Beispiel-KPI',
        'Heating energy prediction {key}': 'Heizenergie-Prognose {key}',
        'Heating energy prediction': 'Heizenergie-Prognose',
        'KPI value selection': 'KPI-Wertauswahl',
        'Measured PV value {key}': 'Gemessener PV-Wert {key}',
        'Measured PV values {key}': 'Gemessene PV-Werte {key}',
        'Measured PV power {key}': 'Gemessene PV-Leistung {key}',
        'Measured PV power history (3 months)': 'PV-Leistungsverlauf (3 Mon.)',
        'Measured PV power history (12 months)': 'PV-Leistungsverlauf (12 Mon.)',
        'Predicted PV value {key} (new)': 'Prognostizierter PV-Wert {key} (neu)',
        'Predicted PV value {key} (recent)': 'Prognostizierter PV-Wert {key} (aktuell)',
        'Predicted PV values {key} (new)': 'Prognostizierte PV-Werte {key} (neu)',
        'Predicted PV values {key} (recent)': 'Prognostizierte PV-Werte {key} (aktuell)',
        'Ratio of Measurement to Prediction': 'Verhältnis von Messung zu Prognose',
        'Saved energy {key}': 'Eingesparte Energie {key}',
        'Saved energy overall': 'Eingesparte Energie insgesamt',
        'Saved energy overall {key}': 'Eingesparte Energie insgesamt {key}',
        'Saved energy last year': 'Eingesparte Energie letztes Jahr',
        'Saved energy last year {key}': 'Eingesparte Energie letztes Jahr {key}',
        'Saved energy last month': 'Eingesparte Energie letzten Monat',
        'Saved energy last month {key}': 'Eingesparte Energie letzten Monat {key}',
        'No historical comparison value is available.': 'Kein historischer Vergleichswert verfügbar.',
        'Show KPI value {index}': 'KPI-Wert {index} anzeigen',
        'Show heating energy prediction': 'Heizenergie-Prognose anzeigen',
        'Show sensor history': 'Sensorverlauf anzeigen',
        'Show sensor history for 3 months': 'Sensorverlauf für drei Monate anzeigen',
        'Show sensor history for 12 months': 'Sensorverlauf für zwölf Monate anzeigen',
        'Show sensor history for 13 months': 'Sensorverlauf für dreizehn Monate anzeigen',
        'Show measured PV power history for 3 months': 'PV-Leistungsverlauf für drei Monate anzeigen',
        'Show measured PV power history for 12 months': 'PV-Leistungsverlauf für zwölf Monate anzeigen',
        'Show power history for 3 months': 'Leistungsverlauf für drei Monate anzeigen',
        'Show power history for 12 months': 'Leistungsverlauf für zwölf Monate anzeigen',
        'Show heating power history for 3 months': 'Wärmeleistungsverlauf für drei Monate anzeigen',
        'Show heating power history for 12 months': 'Wärmeleistungsverlauf für zwölf Monate anzeigen',
        'Sensor history': 'Sensorverlauf',
        'Sensor history (3 months)': 'Sensorverlauf (3 Mon.)',
        'Sensor history (12 months)': 'Sensorverlauf (12 Mon.)',
        'Sensor history (13 months)': 'Sensorverlauf (13 Mon.)',
        'Sensor history {key}': 'Sensorverlauf {key}',
        'Weekend reduction {key}': 'Wochenendabsenkung {key}',
        'Weekend reduction': 'Wochenendabsenkung',
        'Weekend reduction value {key}': 'Wert der Wochenendabsenkung {key}',

        // Debug, Request History & Technical Details
        '#chapter-19': 'Debug, Request History & Technical Details',
        'API Calls': 'API-Aufrufe',
        'API Demos': 'API-Demos',
        'API Requests': 'API-Anfragen',
        'Beautify JSON': 'JSON formatieren',
        'Browse configured devices and their available sensors.': 'Durchsuche konfigurierte Geräte und ihre verfügbaren Sensoren.',
        'Convert to Plotly JSON': 'In Plotly-JSON konvertieren',
        'Enter a Mermaid definition and render it with the shared StaticMermaidDiagram component.': 'Gib eine Mermaid-Definition ein und rendere sie mit der gemeinsamen Komponente StaticMermaidDiagram.',
        'Enter Markdown text and render it with the shared StaticMarkdown component.': 'Gib Markdown-Text ein und rendere ihn mit der gemeinsamen Komponente StaticMarkdown.',
        'Enter Plotly JSON or a Chart.js configuration and render it with the shared StaticStoredPlotlyPlot component.': 'Gib Plotly-JSON oder eine Chart.js-Konfiguration ein und rendere sie mit der gemeinsamen Komponente StaticStoredPlotlyPlot.',
        'Invalid chart configuration': 'Ungültige Diagrammkonfiguration',
        'Invalid Plotly JSON.': 'Ungültiges Plotly-JSON.',
        'Mermaid': 'Mermaid',
        'Mermaid source': 'Mermaid-Quellcode',
        'Markdown source': 'Markdown-Quellcode',
        'Plotly JSON': 'Plotly-JSON',
        'Plotly or Chart.js JSON': 'Plotly- oder Chart.js-JSON',
        'Plotly JSON must describe an object.': 'Plotly-JSON muss ein Objekt beschreiben.',
        'Render': 'Rendern',
        'Reference & Tools': 'Referenz & Werkzeuge',
        'Renderer Demos': 'Renderer-Demos',
        'Clear error log': 'Fehler-Log löschen',
        'Error Log': 'Fehler-Log',
        'Error Diagnostics': 'Fehlerdiagnose',
        'Market & Prices': 'Markt & Preise',
        'No errors have been logged yet.': 'Es wurden noch keine Fehler protokolliert.',
        'Occurred at': 'Aufgetreten am',
        'Review application errors saved locally in this browser.': 'Überprüfe lokal in diesem Browser gespeicherte Anwendungsfehler.',
        'Source': 'Quelle',
        'Stores application errors locally to support troubleshooting without browser console output.': 'Speichert Anwendungsfehler lokal, um die Fehlersuche ohne Browser-Konsole zu unterstützen.',
        'Inspect API requests made during the current browser session.': 'Untersuche API-Anfragen der aktuellen Browsersitzung.',
        'n/a': 'k. A.',
        'Original request': 'Originalanfrage',
        'Overviews': 'Übersichten',
        'Other parameters': 'Weitere Parameter',
        'Parameters': 'Parameter',
        'Raw response preview': 'Vorschau der Rohantwort',
        'Request #{id}': 'Anfrage #{id}',
        'Select a debug tool to inspect the current application state:': 'Wähle ein Debug-Werkzeug, um den aktuellen Anwendungsstatus zu untersuchen:',

        // Contact & Company
        '#chapter-20': 'Contact & Company',
        'Address': 'Adresse',
        'Company': 'Unternehmen',
        'Country': 'Land',
        'Elev {value}': 'Höhe {value}',
        'Email': 'E-Mail',
        'Get in touch with EA Systems Dresden GmbH using the contact details below.': 'Kontaktieren Sie die EA Systems Dresden GmbH über die folgenden Kontaktdaten.',
        'Head office and primary contact': 'Hauptsitz und zentrale Kontaktstelle',
        'Lat {value}': 'Breite {value}',
        'Lng {value}': 'Länge {value}',
        'Location': 'Standort',
        'Official company information and legal notices for EA Systems Dresden GmbH.': 'Offizielle Unternehmensangaben und rechtliche Hinweise der EA Systems Dresden GmbH.',
        'Persons:': 'Personen:',
        'Phone': 'Telefon',
        'Quick overview': 'Kurzüberblick',
        'Source-aligned company information': 'Unternehmensangaben gemäß offizieller Quelle',
        'Website': 'Internetseite',

        // Legal & Imprint
        '#chapter-21': 'Legal & Imprint',
        'Detailed legal information from the company imprint': 'Detaillierte rechtliche Informationen aus dem Impressum des Unternehmens',
        'Key registration and tax details': 'Wesentliche Register- und Steuerangaben',
        'Legal notices': 'Rechtliche Hinweise',
        'Legal overview': 'Rechtlicher Überblick',

        // Versions & Dependencies
        '#chapter-22': 'Versions & Dependencies',
        'Application version': 'Anwendungsversion',
        'Baseline release information': 'Grundlegende Release-Informationen',
        'City of Münster': 'Stadt Münster',
        'Core dependencies': 'Kernabhängigkeiten',
        'Current application version, selected dependency versions, and data licenses.': 'Aktuelle Anwendungsversion, ausgewählte Abhängigkeitsversionen und Datenlizenzen.',
        'Current application version and selected dependency versions from package.json.': 'Aktuelle Anwendungsversion und ausgewählte Abhängigkeitsversionen aus der package.json.',
        'Data licenses': 'Daten-Lizenzen',
        'Data Licence Germany – Attribution – Version 2.0 (dl-de/by-2-0)': 'Datenlizenz Deutschland – Namensnennung – Version 2.0 (dl-de/by-2-0)',
        'Data source': 'Datenquelle',
        'Dataset': 'Datensatz',
        'Dependency': 'Abhängigkeit',
        'Detailed release history can be added here later.': 'Eine detaillierte Release-Historie kann hier später ergänzt werden.',
        'Final energy consumption in the City of Münster': 'Endenergieverbrauch in der Stadt Münster',
        'Rendered directly from the project CHANGELOG.md file.': 'Direkt aus der Datei CHANGELOG.md des Projekts gerendert.',
        'Selected package versions relevant to the current UI stack': 'Ausgewählte Paketversionen, die für den aktuellen UI-Stack relevant sind',
        'The data is processed within EnObserve and prepared for visualization and analysis.': 'Die Daten werden innerhalb von EnObserve verarbeitet und für Visualisierung sowie Auswertungen aufbereitet.',
        'This project uses open-data datasets. The respective data sources and license conditions are listed below.': 'Dieses Projekt verwendet Open-Data-Datensätze. Die jeweiligen Datenquellen und Lizenzbedingungen werden nachfolgend aufgeführt.',
        'Updated at {date}': 'Aktualisiert am {date}',
        'Version history': 'Versionshistorie',

        // System Dashboard
        '#chapter-23': 'System Dashboard',
        '{count} configured sensors': '{count} konfigurierte Sensoren',
        'Acknowledged': 'Quittiert',
        'Average water loss last 7 days': 'Wasserverlust letzte 7 Tage',
        'Average water loss last day': 'Wasserverlust letzter Tag',
        'Active': 'Aktiv',
        'Active / Generation': 'Aktiv / Erzeugung',
        'Basic Consumption': 'Grundverbrauch',
        'Consumption': 'Verbrauch',
        'Data Outage': 'Datenausfall',
        'Data type': 'Datentyp',
        'Electricity': 'Strom',
        'Energy': 'Energie',
        'Export PDF': 'Export PDF',
        'Grid Supply': 'Strom',
        'Heat': 'Wärme',
        'Link': 'Link',
        'Night Setback': 'Nachtabsenkung',
        'Note: This is only a mockup.': 'Hinweis: Das ist nur ein Mockup.',
        'Peak Load': 'Spitzenlast',
        'Performance Reduction': 'Minderertrag',
        'Photovoltaics': 'Photovoltaik',
        'Price Indicator': 'Preisampel',
        'Report': 'Report',
        'Savings Potential': 'Einsparpotenzial',
        'Shutdown': 'Abschaltung',
        'Simultaneous Heating and Cooling': 'Wärme/Kälte gleichzeitig',
        'Sporadic': 'sporadisch',
        'System overview "{key}"': 'Übersicht "{key}"',
        'System information': 'System-Informationen',
        'View sensor details': 'Sensor-Details anzeigen',
        'View system information': 'System-Informationen anzeigen',
        'Water': 'Wasser',
        'Water consumption {key}': 'Wasserverbrauch {key}',
        'Water flow rate {key}': 'Wasserverbrauchsrate {key}',
        'Water loss': 'Wasserverlust',
        'Water loss {key}': 'Wasserverlust {key}',
        'Water loss overall': 'Wasserverlust insgesamt',
        'Water loss last year': 'Wasserverlust letztes Jahr',
        'Water loss last month': 'Wasserverlust letzten Monat',
        'Water loss dashboard': 'Wasserverlust-Dashboard',
        'Water loss overview': 'Wasserverlustübersicht',
        'Water volume {key}': 'Wasservolumen {key}',
        'Flow rate in m³/h': 'Durchfluss in m³/h',
        'Volume in m³': 'Volumen in m³',
        'Volume': 'Volumen',
        'Weekend Setback': 'Wochenendabsenkung',

        // Footer
        '#chapter-24': 'Footer',
        'Footer navigation': 'Footer-Navigation',
    },
    en: {
        'Water loss last 12 complete months': 'Water loss last 12 months',
        'Water loss previous month': 'Water loss previous month',
        'Monthly water loss': 'Monthly water loss',
        'Water loss in m³': 'Water loss in m³',
        'Period: {from} – {to}': 'Period: {from} – {to}',
        ...anomalyTranslations.en,
        ...electricityPriceTranslations.en,
        'Current sensor': 'Current sensor',
        'Show technical details': 'Show technical details',
        'Value display sensor': 'Value display sensor',
    },
    es: {
        'Water loss last 12 complete months': 'Pérdida de agua últimos 12 meses',
        'Water loss previous month': 'Pérdida de agua del mes anterior',
        'Monthly water loss': 'Pérdida mensual de agua',
        'Water loss in m³': 'Pérdida de agua en m³',
        'Period: {from} – {to}': 'Periodo: {from} – {to}',
        ...anomalyTranslations.es,
        ...electricityPriceTranslations.es,

        // General Terms
        '#chapter-1': 'General Terms',
        '1 January {year}': '1 de enero de {year}',
        'All': 'Todas',
        'Automatically converted': 'Convertido automáticamente',
        'Available': 'Disponible',
        'Chart selection': 'Selección de gráfico',
        'Code': 'Código',
        'Code type': 'Tipo de código',
        'Config': 'Configuración',
        'Date': 'Fecha',
        'Day': 'Día',
        'Display': 'Visualización',
        'Direct': 'Directo',
        'Displayed percentage source: {valueSensor}. Traffic light source: {statusSensor}.': 'Fuente del porcentaje mostrado: {valueSensor}. Fuente del color del semáforo: {statusSensor}.',
        'Date type': 'Tipo de fecha',
        'Detected format:': 'Formato detectado:',
        'Electricity [MWh]': 'Electricidad [MWh]',
        'Energy loss difference (1 year)': 'Diferencia de pérdida de energía (1 año)',
        'Electricity price': 'Precio de la electricidad',
        'Entries': 'Entradas',
        'Etc.': 'Etc.',
        'German': 'Alemán',
        'Grid Feed': 'Inyección a red',
        'Group by': 'Agrupar por',
        'Heat [MWh]': 'Calor [MWh]',
        'Individual': 'Individual',
        'Information': 'Información',
        'Interval': 'Intervalo',
        'Key / English': 'Clave / Inglés',
        'Links': 'Enlaces',
        'Loaded format': 'Formato cargado',
        'License': 'Licencia',
        'Meter': 'Contador',
        'Month': 'Mes',
        'Monthly energy in Wh': 'Energía mensual en Wh',
        'Measurement': 'Medición',
        'Prediction': 'Predicción',
        'Series': 'Series',
        'Monthly': 'Mensual',
        'Note': 'Nota',
        'Module 1': 'Módulo 1',
        'Module 2': 'Módulo 2',
        'Name': 'Nombre',
        'No': 'No',
        'No outage': 'Sin fallo',
        'Not available': 'No disponible',
        'None': 'Ninguna',
        'Property': 'Propiedad',
        'Now': 'Ahora',
        'of': 'de',
        'Placeholder': 'Marcador',
        'PV [MWh]': 'FV [MWh]',
        'Reference time': 'Momento de referencia',
        'Rule': 'Regla',
        'Show technical details': 'Mostrar detalles técnicos',
        'Spanish': 'Español',
        'Storage usage': 'Uso de almacenamiento',
        'Significant outage': 'Fallo significativo',
        'Supply type': 'Tipo de suministro',
        'Supply types': 'Tipos de suministro',
        'To': 'Hasta',
        'Unknown': 'Desconocido',
        'Minor outage': 'Fallo menor',
        'URL': 'URL',
        'URN': 'URN',
        'Used': 'Usado',
        'Value': 'Valor',
        'Value display sensor': 'Sensor para mostrar el valor',
        'Version': 'Versión',
        'Water [m³]': 'Agua [m³]',
        'Year': 'Año',
        'Yearly': 'Anual',
        'Yes': 'Sí',

        // General Actions & Controls
        '#chapter-2': 'General Actions & Controls',
        'Add sensor': 'Agregar sensor',
        'Ascending': 'Ascendente',
        'Clear cache': 'Borrar caché',
        'Close': 'Cerrar',
        'Configuration for': 'Configuración para',
        'Descending': 'Descendente',
        'Enable advanced view': 'Activar vista avanzada',
        'Enable debug section': 'Activar sección de depuración',
        'Enable demo section': 'Activar sección de demostración',
        'Enable tools section': 'Activar sección de herramientas',
        'Enabled': 'Activados',
        'Export PDF file': 'Exportar PDF',
        'Foundation': 'Fundamento',
        'Gas': 'Gas',
        'No sorting': 'Sin ordenación',
        'Open demo': 'Abrir demostración',
        'Open info': 'Abrir información',
        'Open price indicator': 'Abrir indicador de precios',
        'Open traffic light legend': 'Abrir leyenda del semáforo',
        'Original endpoint': 'Endpoint original',
        'Original endpoints': 'Endpoints originales',
        'Read data': 'Leer datos',
        'Reset': 'Restablecer',
        'Reset selection and sorting': 'Restablecer selección y ordenación',
        'Save data': 'Guardar datos',
        'Show version': 'Mostrar versión',
        'Sort': 'Ordenación',
        'View statistics': 'Ver estadísticas',

        // Primary Navigation & Areas
        '#chapter-3': 'Primary Navigation & Areas',
        'Account': 'Account',
        'Advanced view': 'Vista avanzada',
        'Appearance': 'Appearance',
        'Breadcrumb': 'Ruta de navegación',
        'Cache': 'Cache',
        'Component Overview': 'Resumen de componentes',
        'Component': 'Componente',
        'Configuration': 'Configuración',
        'Configuration Demo': 'Demo de configuración',
        'Contact': 'Contacto',
        'Customer': 'Cliente',
        'Dashboard': 'Dashboard',
        'Day Ahead and forecast in the unit [€/MWh].': 'Day Ahead y previsión en la unidad [€/MWh].',
        'Day-Ahead Price': 'Precio day-ahead',
        'Debug': 'Depuración',
        'Debug Login': 'Acceso de depuración',
        'Debug section': 'Sección de depuración',
        'Debugging': 'Depuración',
        'Demo': 'Demo',
        'Demo section': 'Sección de demostración',
        'Device Definitions': 'Definiciones de dispositivos',
        'Example Dashboards': 'Dashboards de ejemplo',
        'Home': 'Inicio',
        'Imprint': 'Aviso legal',
        'Language': 'Idioma',
        'Last Value': 'Último valor',
        'Last Value Demo': 'Demo de último valor',
        'Layouts': 'Diseños',
        'Login': 'Acceso',
        'Login Demo': 'Demo de acceso',
        'Logout': 'Cerrar sesión',
        'Page': 'Pagina',
        'Plotly': 'Plotly',
        'Plotly Demo': 'Demo de Plotly',
        'Price Traffic Light': 'Semáforo de precios',
        'Session': 'Session',
        'Statistics': 'Estadísticas',
        'Statuses': 'Estados',
        'System': 'Sistema',
        'Systems': 'Sistemas',
        'Thermal Conflict': 'Conflicto térmico',
        'Time Series': 'Serie temporal',
        'Time Series Demo': 'Demo de series temporales',
        'Typography': 'Typography',
        'Translations': 'Traducciones',
        'Tools': 'Herramientas',
        'Tools section': 'Sección de herramientas',
        'Versions': 'Versiones',
        'Versions & Licenses': 'Versiones y licencias',

        // Configuration Demo
        '#chapter-4': 'Configuration Demo',
        'Always sent as array by saveConfiguration().': 'saveConfiguration() siempre lo envía como array.',
        'All configurations': 'Todas las configuraciones',
        'Add note': 'Agregar nota',
        'Add pause': 'Agregar pausa',
        'Anomaly configurations': 'Configuraciones de anomalías',
        'Author': 'Autor',
        'Configuration data': 'Datos de configuración',
        'Configuration data is loading.': 'Cargando datos de configuración.',
        'Configuration data is loading {completed}/{total}.': 'Cargando datos de configuración {completed}/{total}.',
        'Configuration data could not be saved.': 'No se pudieron guardar los datos de configuración.',
        'Configuration data does not match the required schema.': 'Los datos de configuración especificados no cumplen con el esquema requerido.',
        'Configuration data editor': 'Editor de datos de configuración',
        'Configuration data JSON': 'Datos de configuración JSON',
        'Configuration data must contain valid JSON.': 'Los datos de configuración deben contener JSON válido.',
        'Configuration data was loaded.': 'Se cargaron los datos de configuración.',
        'Configuration data was saved.': 'Se guardaron los datos de configuración.',
        'Configuration request failed: {message}': 'La solicitud de configuración falló: {message}',
        'Customer used to identify the configuration.': 'Cliente utilizado para identificar la configuración.',
        'Created at': 'Creado el',
        'Edit the configuration through form controls. Changes are synchronized with the JSON data below.': 'Edita la configuración mediante controles de formulario. Los cambios se sincronizan con los datos JSON siguientes.',
        'Ends at': 'Finaliza el',
        'Note content': 'Contenido de la nota',
        'Notes': 'Notas',
        'No configuration data exists for the selected customer and system.': 'No hay datos de configuración para el cliente y sistema seleccionados.',
        'No data available.': 'No hay datos disponibles.',
        'Pause reason': 'Motivo de la pausa',
        'Pauses': 'Pausas',
        'Read and save arbitrary JSON configuration data for a customer and system. Configuration reads and writes use JSON requests and are not cached.': 'Lee y guarda datos de configuración JSON arbitrarios para un cliente y un sistema. Las lecturas y escrituras usan solicitudes JSON y no se almacenan en caché.',
        'Read all available customer and system configurations without editing them.': 'Lee todas las configuraciones disponibles de clientes y sistemas sin posibilidad de edición.',
        'Read-only overview of all available customer and system configurations.': 'Vista de solo lectura de todas las configuraciones disponibles de clientes y sistemas.',
        'Read configuration': 'Leer configuración',
        'Read the saved JSON through data.data. When no configuration exists, data is null and success is false.': 'Lee el JSON guardado mediante data.data. Cuando no existe configuración, data es null y success es false.',
        'Read, edit, and save JSON configuration data for a customer and system.': 'Lee, edita y guarda datos de configuración JSON para un cliente y sistema.',
        'Reading configuration data...': 'Leyendo datos de configuración...',
        'Save configuration': 'Guardar configuración',
        'Sensor configurations': 'Configuraciones de sensores',
        'Sensor URN': 'URN del sensor',
        'Saving configuration data...': 'Guardando datos de configuración...',
        'Settings': 'Configuración',
        'Starts at': 'Comienza el',
        'System used to identify the configuration.': 'Sistema utilizado para identificar la configuración.',
        'The client always sends data_type: array and stores the supplied JSON string as configuration content.': 'El cliente siempre envía data_type: array y guarda la cadena JSON indicada como contenido de configuración.',
        'The tags object requires customer and system. A missing configuration returns success: false and can be handled as an empty object.': 'El objeto tags requiere customer y system. Una configuración ausente devuelve success: false y puede tratarse como un objeto vacío.',
        'The JSON configuration must be valid to use the editor.': 'La configuración JSON debe ser válida para utilizar el editor.',
        'Remove': 'Eliminar',
        'Valid JSON string to save. Only required for saveConfiguration().': 'Cadena JSON válida para guardar. Solo se requiere para saveConfiguration().',

        // Component Overview & Source Actions
        '#chapter-5': 'Component Overview & Source Actions',
        'Application files': 'Archivos de la aplicación',
        'Browse the application structure and its main components.': 'Consulta la estructura de la aplicación y sus componentes principales.',
        'Browse all project translation keys and their German and Spanish values.': 'Consulta todas las claves de traducción del proyecto con sus valores en alemán y español.',
        'Components': 'Componentes',
        'Contexts': 'Contextos',
        'Copied': 'Copiado',
        'Copy code': 'Copiar código',
        'Copy Markdown source': 'Copiar código fuente Markdown',
        'Copy Mermaid source': 'Copiar código fuente Mermaid',
        'Download diagram': 'Descargar diagrama',
        'Download Markdown source': 'Descargar código fuente Markdown',
        'Download Mermaid source': 'Descargar código fuente Mermaid',
        'Explore how application routes are connected to their React components.': 'Explora como las rutas de la aplicacion estan conectadas con sus componentes de React.',
        'External components': 'Componentes externos',
        'Legend': 'Leyenda',
        'Markdown': 'Markdown',
        'MermaidDiagram': 'MermaidDiagram',
        'no project path': 'sin ruta de proyecto',
        'Routes': 'Rutas',
        'Search pages': 'Buscar páginas',
        'StoredPlotlyPlot': 'StoredPlotlyPlot',
        'TimeSeriesPlot': 'TimeSeriesPlot',
        'TimeSeriesValue': 'TimeSeriesValue',
        'Unable to download diagram.': 'No se pudo descargar el diagrama.',
        'URL path': 'Ruta URL',
        'Workspace': 'Espacio de trabajo',

        // Overview, Sensors & Metrics
        '#chapter-6': 'Overview, Sensors & Metrics',
        '{count} sensor': '{count} sensor',
        '{count} sensors': '{count} sensores',
        'Active sensors only': 'Solo sensores activos',
        'Aggregation': 'Agregación',
        'All sensors': 'Todos los sensores',
        'Anomalies': 'Anomalías',
        'Anomaly': 'Anomalía',
        'Average': 'Promedio',
        'Base Load': 'Carga base',
        'Base Load Night': 'Carga base nocturna',
        'Base Load Overview': 'Resumen de carga base',
        'Category': 'Categoría',
        'Current': 'Actual',
        'Current sensor': 'Sensor actual',
        'Current value': 'Valor actual',
        'Current Value': 'Valor actual',
        'Current Value Demo': 'Demo de valor actual',
        'Cumulative water loss {key}': 'Pérdida de agua acumulada {key}',
        'Customers': 'Clientes',
        'Cumulative water loss in m³': 'Pérdida de agua acumulada en m³',
        'Danger sensor count': 'Número de sensores en peligro',
        'Date used for the Münster data link.': 'Fecha para el enlace de datos de Münster.',
        'Description': 'Descripción',
        'Detail': 'Detalle',
        'Details': 'Detalles',
        'DTC error code': 'Código de error DTC',
        'DTC value for the last day': 'Valor DTC del último día',
        'DTC value over the last 7 days': 'Valor DTC de los últimos 7 días',
        'Energy sensor': 'Sensor de energía',
        'Example sensor name': 'Nombre de sensor de ejemplo',
        'Filter': 'Filtro',
        'Group': 'Grupo',
        'Group / supply type': 'Grupo / tipo de suministro',
        'Group sensor/device selection by': 'Agrupar la selección de sensores/dispositivos por',
        'Heating Energy': 'Energía térmica',
        'Heating Energy Overview': 'Resumen de energía térmica',
        'Interval Values': 'Valores de intervalo',
        'Interval Values Demo': 'Demo de valores de intervalo',
        'KPI': 'KPI',
        'KPIs': 'KPIs',
        'Link to the monitoring server': 'Enlace al servidor de monitorización',
        'Link to the Münster data request for date {date}': 'Enlace a la consulta de datos de Münster para la fecha {date}',
        'Maximum': 'Máximo',
        'Medium': 'Medio',
        'Münster data': 'Datos de Münster',
        'Münster': 'Münster',
        'Neutraubling': 'Neutraubling',
        'No sensor/device is available for the current selection.': 'No hay ningún sensor o dispositivo disponible para la selección actual.',
        'Overview': 'Resumen',
        'Overview of all available anomaly metrics.': 'Resumen de todas las métricas de anomalías disponibles.',
        'Overview of all available base load metrics.': 'Resumen de todas las métricas de carga base disponibles.',
        'Overview of all available customers.': 'Resumen de todos los clientes disponibles.',
        'Overview of all available heating energy metrics.': 'Resumen de todas las métricas de energía térmica disponibles.',
        'Overview of all available PV prediction metrics.': 'Resumen de todas las métricas de predicción FV disponibles.',
        'Overview of all available statuses.': 'Resumen de todos los estados disponibles.',
        'Overview of all available systems.': 'Resumen de todos los sistemas disponibles.',
        'Outdated': 'Obsoleto',
        'PV Prediction': 'Predicción FV',
        'PV Prediction Overview': 'Resumen de predicción FV',
        'Power sensor': 'Sensor de potencia',
        'Raw response': 'Respuesta sin procesar',
        'Select a sensor/device to inspect all available sensor names and links.': 'Seleccione un sensor o dispositivo para consultar todos los nombres de sensores y enlaces disponibles.',
        'Sensor': 'Sensor',
        'Sensor / device': 'Sensor / dispositivo',
        'Sensor count': 'Número de sensores',
        'Sensor name': 'Nombre del sensor',
        'Sensor type': 'Tipo de sensor',
        'Sensors': 'Sensores',
        'Sensors / Devices': 'Sensores / dispositivos',
        'State': 'Estado',
        'Status': 'Estado',
        'Subsensor': 'Subsensor',
        'This is the primary sensor configured for this device.': 'Este es el sensor principal configurado para este dispositivo.',
        'Success sensor count': 'Número de sensores correctos',
        'Sum': 'Suma',
        'Supply type / group': 'Tipo de suministro / grupo',
        'Unit': 'Unidad',
        'Virtual devices': 'Dispositivos virtuales',
        'View': 'Vista',
        'Volume flow sensor': 'Sensor de caudal',
        'Water loss over the last 7 days in m³/h': 'Pérdida de agua de los últimos 7 días en m³/h',
        'Water loss last 7 days': 'Pérdida de agua últimos 7 días',
        'Warning sensor count': 'Número de sensores con advertencia',

        // General Feedback & Loading
        '#chapter-7': 'General Feedback & Loading',
        'Current value was loaded.': 'Se cargó el valor actual.',
        'Current values were loaded.': 'Se cargaron los valores actuales.',
        'Last value was loaded.': 'Se cargó el valor.',
        'Last values were loaded.': 'Se cargaron los valores.',
        'Loading API request {current}/{total}': 'Cargando solicitud API {current}/{total}',
        'Loading API request...': 'Cargando solicitud API...',
        'Loading...': 'Cargando...',
        'No pages found.': 'No se encontraron páginas.',
        'Plotly JSON was loaded.': 'Se cargó el JSON de Plotly.',
        'This feature will be available soon.': 'Esta función estará disponible próximamente.',
        'Time series was loaded.': 'Se cargó la serie temporal.',
        'Unable to load Münster data: {message}': 'No se pudieron cargar los datos de Münster: {message}',

        // Status & Availability
        '#chapter-8': 'Status & Availability',
        'Attention: This is a disabled sensor. The request may not return any time series data.': 'Atención: Este es un sensor desactivado. Es posible que la solicitud no devuelva datos de series temporales.',
        'Attention: This is a disabled sensor. Some sensor time series in the following table may not be available.': 'Atención: Este es un sensor desactivado. Es posible que algunas series temporales de sensores de la siguiente tabla no estén disponibles.',
        'Coming soon': 'Próximamente',
        'Disabled sensor': 'Sensor desactivado',
        'Displayed value was converted.': 'El valor mostrado se ha convertido.',
        'Critical': 'Crítico',
        'Error': 'Error',
        'Expensive': 'Caro',
        'Favourable': 'Económico',
        'General Error': 'Error general',
        'Implausible': 'Implausible',
        'Inactive': 'Inactivo',
        'No sensor or sensors available': 'No hay ningún sensor disponible',
        'Normal': 'Normal',
        'Not implemented': 'No implementado',
        'Not implemented yet.': 'Aún no implementado.',
        'Not monitored': 'No supervisado',
        'Not supported': 'No compatible',
        'OK': 'OK',
        'Otherwise': 'En caso contrario',
        'Performance Deviation': 'Rendimiento anómalo',
        'Sensor enabled': 'Sensor activado',
        'Sensor not available or not enabled': 'Sensor no disponible o no activado',
        'Sensor not enabled': 'Sensor no activado',
        'Source values': 'Valores de origen',
        'Status calculation': 'Cálculo de estado',
        'Traffic light legend': 'Leyenda del semáforo',
        'Warning': 'Advertencia',

        // Session & Authentication
        '#chapter-9': 'Session & Authentication',
        'API token acquired successfully.': 'Token de API obtenido correctamente.',
        'API token check completed successfully. ({durationMs} ms)': 'La comprobación del token de API se completó correctamente. ({durationMs} ms)',
        'API token check failed. Session was cleared.': 'La comprobación del token de API falló. La sesión fue eliminada.',
        'API token check failed: {message}': 'La comprobación del token de API falló: {message}',
        'API token check succeeded. Session appears valid.': 'La comprobación del token de API fue correcta. La sesión parece válida.',
        'API token likely expired': 'El token de API probablemente ha caducado',
        'Check API token': 'Comprobar token de API',
        'Check API token output': 'Salida de comprobación del token de API',
        'Check, renew, or clear the current API token.': 'Comprueba, renueva o elimina el token de API actual.',
        'Checking API token...': 'Comprobando token de API...',
        'Current API token': 'Token de API actual',
        'Failed to renew API token: {message}': 'No se pudo renovar el token de API: {message}',
        'Go to Login': 'Ir al acceso',
        'Hide password': 'Ocultar contraseña',
        'Imported at {date}': 'Importado el {date}',
        'Invalid username or password': 'Nombre de usuario o contraseña no válidos.',
        'Logged in': 'Sesión iniciada',
        'Logged in as': 'Sesión iniciada como',
        'Logged out': 'Sesión cerrada',
        'Login failed: {message}': 'Error de inicio de sesión: {message}',
        'No API request executed yet.': 'Todavía no se ha ejecutado ninguna solicitud API.',
        'No API token available.': 'No hay ningún token de API disponible.',
        'Password': 'Contraseña',
        'Refreshing API token...': 'Renovando token de API...',
        'Refreshing session...': 'Actualizando sesión...',
        'Renew Login': 'Renovar acceso',
        'Renew API token': 'Renovar token de API',
        'Session status': 'Estado de la sesión',
        'Show password': 'Mostrar contraseña',
        'Sign in': 'Iniciar sesión',
        'Sign in to continue': 'Inicie sesión para continuar',
        'Signing in...': 'Iniciando sesión...',
        'The API token check failed because the current API token appears to be expired. Renew the login and try again.': 'La comprobación del token de API falló porque el token actual parece haber caducado. Renueva el inicio de sesión e inténtalo de nuevo.',
        'The API token check response will be shown here.': 'La respuesta de comprobación del token de API se mostrará aquí.',
        'The current API token appears to be no longer valid and should be renewed.': 'El token de API actual parece no ser válido y debería renovarse.',
        'Toggle light and dark theme': 'Cambiar entre tema claro y oscuro',
        'Unknown login error.': 'Error de inicio de sesión desconocido.',
        'Username': 'Usuario',
        'Using API token restored from session storage.': 'Se está usando el token de API restaurado desde el almacenamiento de sesión.',

        // Requests & Generic Errors
        '#chapter-10': 'Requests & Generic Errors',
        'API request failed: {message}': 'La solicitud API falló: {message}',
        'Current value request failed.': 'La solicitud del valor actual falló.',
        'Current value response is not a CSV string.': 'La respuesta del valor actual no es una cadena CSV.',
        'Last value request failed.': 'La solicitud del último valor falló.',
        'Last value response is not a CSV string.': 'La respuesta del último valor no es una cadena CSV.',
        'Missing current value request parameters.': 'Faltan parámetros de solicitud del valor actual.',
        'Missing last value request parameters.': 'Faltan parámetros de solicitud del último valor.',
        'Missing Plotly request parameters.': 'Faltan parámetros de solicitud de Plotly.',
        'Missing time series request parameters.': 'Faltan parámetros de solicitud de series temporales.',
        'No interval values were found for sensors {sensors}.': 'No se encontraron valores de intervalo para los sensores {sensors}.',
        'Plotly request failed.': 'La solicitud de Plotly falló.',
        'Request status': 'Estado de la solicitud',
        'Requesting example dataset...': 'Solicitando conjunto de datos de ejemplo...',
        'Requesting last value dataset...': 'Solicitando conjunto de datos del último valor...',
        'Requesting last value...': 'Solicitando último valor...',
        'Requesting last values...': 'Solicitando últimos valores...',
        'Requesting Plotly JSON...': 'Solicitando JSON de Plotly...',
        'Requesting time series dataset...': 'Solicitando conjunto de datos de series temporales...',
        'Requesting time series...': 'Solicitando series temporales...',
        'Sensor {sensor} returned no numeric current value.': 'El sensor {sensor} no devolvió ningún valor actual numérico.',
        'Sensor {sensor} was not found.': 'No se encontró el sensor {sensor}.',
        'The Plotly request failed because the current API token appears to be expired. Renew the login and try again.': 'La solicitud de Plotly falló porque el token de API actual parece haber caducado. Renueva el inicio de sesión e inténtalo de nuevo.',
        'The time series request failed because the current API token appears to be expired. Renew the login and try again.': 'La solicitud de series temporales falló porque el token de API actual parece haber caducado. Renueva el inicio de sesión e inténtalo de nuevo.',
        'Time series request failed.': 'La solicitud de series temporales falló.',
        'Time series response is not a CSV string.': 'La respuesta de series temporales no es una cadena CSV.',
        'Unknown request error.': 'Error de solicitud desconocido.',

        // Page Descriptions & Navigation
        '#chapter-11': 'Page Descriptions & Navigation',
        '{anomalyCount} anomalies from {sensorCount} sensor found: {details}': '{anomalyCount} anomalías de {sensorCount} sensor encontradas: {details}',
        '{anomalyCount} anomalies from {sensorCount} sensors found: {details}': '{anomalyCount} anomalías de {sensorCount} sensores encontradas: {details}',
        '{anomalyCount} anomaly from {sensorCount} sensor found: {details}': '{anomalyCount} anomalía de {sensorCount} sensor encontrada: {details}',
        '{anomalyCount} anomaly from {sensorCount} sensors found: {details}': '{anomalyCount} anomalía de {sensorCount} sensores encontrada: {details}',
        '{sensorCount} {anomaly} sensor': '{sensorCount} sensor de {anomaly}',
        '{sensorCount} {anomaly} sensors': '{sensorCount} sensores de {anomaly}',
        'Base Load {key}': 'Carga base {key}',
        'Base load anomaly monitoring for energy-cost deviations and baseline behavior.': 'Supervisión de anomalías de carga base para desviaciones de costes energéticos y comportamiento de referencia.',
        'Base load dashboard': 'Dashboard de carga base',
        'Heating Energy {key}': 'Energía térmica {key}',
        'Heating energy anomaly monitoring for temperatures, reductions, and saved energy.': 'Supervisión de anomalías de energía térmica para temperaturas, reducciones y energía ahorrada.',
        'Heating energy dashboard': 'Dashboard de energía térmica',
        'Home navigation': 'Navegación de inicio',
        'Metrics from customer {customer}.': 'Métricas del cliente {customer}.',
        'Metrics from system {system}.': 'Métricas del sistema {system}.',
        'Metrics with {status} status.': 'Métricas con estado {status}.',
        'No anomalies found for {sensorCount} sensor.': 'No se encontraron anomalías de {sensorCount} sensor.',
        'No anomalies found for {sensorCount} sensors.': 'No se encontraron anomalías de {sensorCount} sensores.',
        'Open base load dashboard for {key}': 'Abrir dashboard de carga base para {key}',
        'Overview dashboard': 'Dashboard de resumen',
        'Photovoltaic': 'Fotovoltaica',
        'PV prediction {key}': 'Predicción FV {key}',
        'PV Prediction {key}': 'Predicción FV {key}',
        'PV prediction anomaly monitoring for feed-in performance, forecasting quality, and operational warnings.': 'Supervisión de anomalías de predicción FV para rendimiento de inyección, calidad de predicción y advertencias operativas.',
        'PV prediction dashboard': 'Dashboard de predicción FV',
        'Select a system to open its metrics:': 'Seleccione un sistema para abrir sus métricas:',
        'Select an anomaly to open its dashboard and related metrics:': 'Seleccione una anomalía para abrir su panel y las métricas relacionadas:',
        'Statistics dashboard': 'Dashboard de estadísticas',
        'Statistics for this system are currently being prepared.': 'Las estadísticas de este sistema se están preparando actualmente.',
        'Statistics overview for system {key}.': 'Resumen de estadísticas para el sistema {key}.',
        'System details for {system} will be available soon.': 'Los detalles del sistema {system} estarán disponibles próximamente.',
        'The day-ahead price shows the wholesale electricity price for the Germany/Luxembourg market area.': 'El precio day-ahead muestra el precio mayorista de la electricidad para la zona de mercado Alemania/Luxemburgo.',
        'Thermal conflict dashboard': 'Dashboard de conflicto térmico',
        'Thermal conflict monitoring for thermal conditions and potential conflicts.': 'Supervisión de conflictos térmicos para condiciones térmicas y posibles conflictos.',

        // Layouts, Typography & Demo Content
        '#chapter-12': 'Layouts, Typography & Demo Content',
        'API': 'API',
        'API2 Data': 'Datos API2',
        'Api2LastValueData and Api2TimeSeriesData provide resolved API data for custom rendering.': 'Api2LastValueData y Api2TimeSeriesData proporcionan datos de API resueltos para representaciones personalizadas.',
        'Browse example dashboard layouts and their responsive behavior.': 'Consulta los diseños de dashboards de ejemplo y su comportamiento adaptable.',
        'Browse UI layout components and implementation examples.': 'Consulta componentes de diseño de interfaz y ejemplos de implementación.',
        'Selected reusable components with implementation examples and rendered previews.': 'Componentes reutilizables seleccionados con ejemplos de implementación y vistas previas renderizadas.',
        'Call example': 'Ejemplo de llamada',
        'Charts, diagrams and structured content components.': 'Componentes para gráficos, diagramas y contenido estructurado.',
        'Configured reference of the stored Plotly figure.': 'Referencia configurada de la figura Plotly guardada.',
        'Acquires or renews the API bearer token used by all authenticated requests.': 'Obtiene o renueva el token bearer de la API usado por todas las solicitudes autenticadas.',
        'Current API bearer token from useSession().': 'Token bearer actual de la API procedente de useSession().',
        'Current values': 'Valores actuales',
        'Dashboard layout primitives and typographic defaults.': 'Elementos básicos de diseño del dashboard y valores tipográficos predeterminados.',
        'Data': 'Datos',
        'Description.': 'Descripción.',
        'End time, for example NOW or a formatted date-time value.': 'Hora de finalización, por ejemplo NOW o un valor de fecha y hora formateado.',
        'Every API operation returns data together with request metadata. The client selects the appropriate transport automatically for developer and live builds.': 'Cada operación API devuelve datos junto con metadatos de la solicitud. El cliente selecciona automáticamente el transporte adecuado para compilaciones de desarrollo y producción.',
        'Example dashboard for visualizing thermal conflicts and related metrics.': 'Dashboard de ejemplo para visualizar conflictos térmicos y métricas relacionadas.',
        'Example overview dashboard with combined KPI cards and responsive grid behavior.': 'Dashboard de resumen de ejemplo con tarjetas KPI combinadas y comportamiento de cuadrícula adaptable.',
        'Example response': 'Respuesta de ejemplo',
        'Example statistics dashboard with chart-focused panels and responsive grid behavior.': 'Dashboard de estadísticas de ejemplo con paneles centrados en gráficos y comportamiento de cuadrícula adaptable.',
        'Interval values': 'Valores de intervalo',
        'KPI API2 Current Value': 'KPI de valor actual API2',
        'KPI API2 Last Value': 'KPI de último valor API2',
        'KPI API2 Time Series': 'KPI de series temporales API2',
        'KPI cards and API data sources for monitoring values.': 'Tarjetas KPI y fuentes de datos API para valores de monitorización.',
        'KPI Static': 'KPI estáticos',
        'KPIs & API Data': 'KPI y datos API',
        'KpiValueApi2LastValue and KpiCarouselApi2LastValue load current API2 values directly.': 'KpiValueApi2LastValue y KpiCarouselApi2LastValue cargan directamente valores actuales de API2.',
        'KpiValueApi2TimeSeries and KpiCarouselApi2TimeSeries load and aggregate API2 time series.': 'KpiValueApi2TimeSeries y KpiCarouselApi2TimeSeries cargan y agregan series temporales de API2.',
        'KpiValueStatic and KpiCarouselStatic render manually provided KPI values.': 'KpiValueStatic y KpiCarouselStatic muestran valores de KPI proporcionados manualmente.',
        'Last values': 'Últimos valores',
        'Layout & Foundations': 'Diseño y fundamentos',
        'Learn how to call the central EE-Check API client from project code.': 'Aprende a llamar al cliente central de la API EE-Check desde el código del proyecto.',
        'List item 1': 'Punto de lista 1',
        'List item 2': 'Punto de lista 2',
        'Loads a saved Plotly figure. The returned data can be passed directly to the shared Plotly components.': 'Carga una figura Plotly guardada. Los datos devueltos se pueden pasar directamente a los componentes Plotly compartidos.',
        'Loads CSV time series data for one or more comma-separated URNs. Large URN lists are split and merged automatically.': 'Carga datos CSV de series temporales para una o más URN separadas por comas. Las listas de URN grandes se dividen y combinan automáticamente.',
        'Loads current values and passes resolved data into a render function without imposing a KPI layout.': 'Carga valores actuales y pasa datos resueltos a una función de renderizado sin imponer un diseño de KPI.',
        'Loads interval differences for one or more comma-separated URNs between two required timestamps.': 'Carga diferencias de intervalo para una o más URN separadas por comas entre dos marcas de tiempo obligatorias.',
        'Loads the current value, or the last value before an optional timestamp, for one or more comma-separated URNs.': 'Carga el valor actual o el último valor antes de una marca de tiempo opcional para una o más URN separadas por comas.',
        'Loads the latest value, or the last value before an optional timestamp, for one or more comma-separated URNs.': 'Carga el último valor, o el último valor antes de una marca de tiempo opcional, para una o más URN separadas por comas.',
        'Login and token renewal': 'Inicio de sesión y renovación del token',
        'Markdown renders lightweight formatted content such as headings, tables, and explanatory notes.': 'Markdown muestra contenido formateado ligero como encabezados, tablas y notas explicativas.',
        'MermaidDiagram renders text-based diagrams such as flowcharts, sequence diagrams, and class diagrams.': 'MermaidDiagram muestra diagramas basados en texto como flujogramas, diagramas de secuencia y diagramas de clases.',
        'One URN or multiple comma-separated URNs.': 'Una URN o varias URN separadas por comas.',
        'One URN or multiple comma-separated URNs. EE-Check function URNs are encoded by the client.': 'Una URN o varias URN separadas por comas. Las URN de funciones EE-Check se codifican mediante el cliente.',
        'Optional interval. The default is 1year; another example is 1month.': 'Intervalo opcional. El valor predeterminado es 1year; otro ejemplo es 1month.',
        'Optional layout overrides for the stored figure.': 'Sobrescrituras de diseño opcionales para la figura guardada.',
        'Optional object with username and password. Without it, configured default credentials are used.': 'Objeto opcional con usuario y contraseña. Sin él se usan las credenciales predeterminadas configuradas.',
        'Optional progress callback with currentRequest and totalRequests.': 'Callback de progreso opcional con currentRequest y totalRequests.',
        'Optional progress callback, especially useful when a large URN list is split.': 'Callback de progreso opcional, especialmente útil cuando se divide una lista grande de URN.',
        'Optional reference time. Omit it for the current last value; examples include NOW and 2026-01-01 00:00:00.': 'Tiempo de referencia opcional. Omítelo para el último valor actual; los ejemplos incluyen NOW y 2026-01-01 00:00:00.',
        'Optional reference time. Omit it for the current value; examples include NOW and 2026-01-01 00:00:00.': 'Tiempo de referencia opcional. Omítelo para el valor actual; los ejemplos incluyen NOW y 2026-01-01 00:00:00.',
        'Parameter': 'Parámetro',
        'Reference for calling the central EE-Check API client from project components.': 'Referencia para llamar al cliente central de la API EE-Check desde componentes del proyecto.',
        'Required': 'Obligatorio',
        'Required end time, for example NOW or a formatted date-time value.': 'Hora de finalización obligatoria, por ejemplo NOW o un valor de fecha y hora formateado.',
        'Required start time, for example 2018-01-01.': 'Hora de inicio obligatoria, por ejemplo 2018-01-01.',
        'Select a customer and group filter to generate virtual device definitions.': 'Seleccione un cliente y un filtro de grupo para generar definiciones de dispositivos virtuales.',
        'Select a demo to explore the available examples and reference pages:': 'Selecciona una demo para explorar los ejemplos y las páginas de referencia disponibles:',
        'Select a tool to explore the available reference pages and utilities:': 'Selecciona una herramienta para explorar las páginas de referencia y utilidades disponibles:',
        'Select an example dashboard to open its responsive dashboard layout:': 'Seleccione un dashboard de ejemplo para abrir su diseño de dashboard adaptable:',
        'Select one layout building block to open its examples and implementation details:': 'Seleccione un bloque de diseño para abrir sus ejemplos y detalles de implementación:',
        'Shared response format': 'Formato de respuesta compartido',
        'Start time, for example NOW-4WEEKS or a formatted date-time value.': 'Hora de inicio, por ejemplo NOW-4WEEKS o un valor de fecha y hora formateado.',
        'Store data.token and pass it to all authenticated data requests.': 'Guarda data.token y pásalo a todas las solicitudes de datos autenticadas.',
        'Stored Plotly data': 'Datos Plotly guardados',
        'Fixed query parameter added automatically by the client.': 'Parámetro de consulta fijo que el cliente añade automáticamente.',
        'Loads a stored chart definition by name. Chart.js responses are converted before rendering, while Plotly responses are used directly.': 'Carga una definición de gráfico guardada por nombre. Las respuestas de Chart.js se convierten antes de renderizarlas, mientras que las respuestas de Plotly se usan directamente.',
        'Name of the stored chart definition to load.': 'Nombre de la definición de gráfico guardada que se debe cargar.',
        'The data field contains Chart.js or Plotly JSON. Convert only Chart.js configurations with ChartJsToPlotlyConverter before using shared Plotly components.': 'El campo data contiene JSON de Chart.js o Plotly. Convierte solo configuraciones de Chart.js con ChartJsToPlotlyConverter antes de usar los componentes Plotly compartidos.',
        'Use this for server-stored chart definitions that should be rendered with the shared Plotly chart components.': 'Usa esto para definiciones de gráficos almacenadas en el servidor que deben representarse con los componentes de gráficos Plotly compartidos.',
        'StoredPlotlyPlot renders ready-made Plotly JSON figures from static input or API requests.': 'StoredPlotlyPlot muestra figuras JSON de Plotly ya preparadas a partir de datos estáticos o solicitudes API.',
        'Tags used to resolve the requested figure, for example meter.': 'Etiquetas usadas para resolver la figura solicitada, por ejemplo meter.',
        'The data field contains CSV text with timestamp, URN, value, and unit. Parse it with parseLastValueCsv().': 'El campo data contiene texto CSV con marca de tiempo, URN, valor y unidad. Analízalo con parseLastValueCsv().',
        'The data field contains CSV text. Parse it with parseTimeSeriesCsv() before rendering or aggregating values.': 'El campo data contiene texto CSV. Analízalo con parseTimeSeriesCsv() antes de renderizar o agregar valores.',
        'The data field contains CSV text. parseIntervalValueCsv() extracts timestamps and sensor columns together with their units.': 'El campo data contiene texto CSV. parseIntervalValueCsv() extrae las marcas de tiempo y las columnas de sensores junto con sus unidades.',
        'TimeSeriesPlot renders parsed time series data as shared Plotly figures from static or API data.': 'TimeSeriesPlot muestra datos de series temporales procesados como figuras Plotly compartidas desde datos estáticos o de API.',
        'TimeSeriesValue renders KPI-style values from static input or aggregated API time series.': 'TimeSeriesValue muestra valores de KPI a partir de datos estáticos o series temporales agregadas desde la API.',
        'Type': 'Tipo',
        'Typography shows the base tag styling for headings and paragraphs from the shared stylesheet layer.': 'Typography muestra el estilo base de etiquetas para encabezados y párrafos desde la capa compartida de estilos.',
        'Use case': 'Caso de uso',
        'Use data.data, data.layout, and data.config as Plotly figure properties.': 'Usa data.data, data.layout y data.config como propiedades de la figura Plotly.',
        'Use this for current KPI values and lightweight overview data without loading a full time series.': 'Usa esto para valores KPI actuales y datos de resumen ligeros sin cargar una serie temporal completa.',
        'Use this for interval-based consumption values without calculating differences from meter readings in the application.': 'Usa esto para valores de consumo por intervalos sin calcular diferencias de lecturas de contador en la aplicación.',
        'Use this for plots, aggregations, and KPI values based on a period of measurements.': 'Usa esto para gráficos, agregaciones y valores KPI basados en un período de mediciones.',
        'Use this for saved server-side Plotly charts, for example through Api2StoredPlotlyPlot.': 'Usa esto para gráficos Plotly guardados en el servidor, por ejemplo mediante Api2StoredPlotlyPlot.',
        'Use this for the login form and for renewing an expired API token.': 'Usa esto para el formulario de acceso y para renovar un token de API caducado.',
        'Visualization & Content': 'Visualización y contenido',

        // Filters, Selection & Form Labels
        '#chapter-13': 'Filters, Selection & Form Labels',
        'Custom date': 'Fecha personalizada',
        'From': 'Desde',
        'Group filter': 'Filtro de grupo',
        'No sensors selected yet.': 'Todavía no se han seleccionado sensores.',
        'No subsensor': 'Sin subsensor',
        'Position:': 'Posición:',
        'Range': 'Rango',
        'Reference': 'Referencia',
        'Remove sensor': 'Eliminar sensor',
        'Selected group': 'Grupo seleccionado',
        'Selected sensor': 'Sensor seleccionado',
        'Selected sensors': 'Sensores seleccionados',
        'Sensor is already in list.': 'El sensor ya está en la lista.',
        'To Date': 'Fecha final',
        'Without group': 'Sin grupo',

        // Plotly
        '#chapter-14': 'Plotly',
        '{meter} using {reference} with {range}': '{meter} usando {reference} con {range}',
        'Loading Plotly chart...': 'Cargando gráfico Plotly...',
        'No Plotly chart available.': 'No hay gráfico Plotly disponible.',
        'No Plotly chart loaded yet.': 'Todavía no se ha cargado ningún gráfico Plotly.',
        'Plotly chart output': 'Salida del gráfico Plotly',
        'Plotly JSON request completed successfully. ({durationMs} ms)': 'La solicitud de JSON de Plotly se completó correctamente. ({durationMs} ms)',
        'Plotly request target': 'Destino de solicitud Plotly',
        'API request payload': 'Solicitud API (solo lectura)',
        'Request and render interactive Plotly chart data.': 'Solicita y representa datos interactivos de gráficos Plotly.',
        'Chart Definition': 'Definición de gráfico',
        'Chart Definitions': 'Definiciones de gráficos',
        'Chart.js JSON': 'JSON de Chart.js',
        'Chart definition request failed: {message}': 'La solicitud de la definición de gráfico falló: {message}',
        'Request stored chart definitions and render them as interactive Plotly charts.': 'Solicita definiciones de gráficos guardadas y las representa como gráficos Plotly interactivos.',
        'Request a stored chart definition and render it as an interactive Plotly chart.': 'Solicita una definición de gráfico guardada y la representa como un gráfico Plotly interactivo.',
        'Request chart definition': 'Solicitar definición de gráfico',
        'Requesting chart definition...': 'Solicitando definición de gráfico...',
        'The API response does not contain a valid chart definition.': 'La respuesta de la API no contiene una definición de gráfico válida.',
        'Request Plotly JSON': 'Solicitar JSON de Plotly',
        'Building statistics': 'Estadísticas del edificio',
        'Building statistics could not be loaded.': 'No se pudieron cargar las estadísticas del edificio.',
        'Building statistics request failed: {message}': 'La solicitud de estadísticas del edificio falló: {message}',
        'Building statistics were loaded.': 'Se cargaron las estadísticas del edificio.',
        'Building system URN, for example Mue.0531 or GN.Building.': 'URN del sistema del edificio, por ejemplo Mue.0531 o GN.Building.',
        'Each supply type is either an object with four aligned value and timestamp series or an empty array when no data is available.': 'Cada tipo de suministro es un objeto con cuatro series alineadas de valores y marcas de tiempo, o un array vacío si no hay datos disponibles.',
        'First value': 'Primer valor',
        'Grid feed': 'Inyección a red',
        'Last value': 'Último valor',
        'Load summarized energy statistics for every available supply type of a system.': 'Carga estadísticas energéticas resumidas para cada tipo de suministro disponible de un sistema.',
        'Loads daily, monthly, and yearly energy statistics for every available supply type of a building.': 'Carga estadísticas energéticas diarias, mensuales y anuales para cada tipo de suministro disponible de un edificio.',
        'Use this for compact building consumption summaries without requesting and calculating every energy interval separately.': 'Utiliza esto para resúmenes compactos del consumo de un edificio sin solicitar ni calcular cada intervalo energético por separado.',
        'No building statistics loaded yet.': 'Aún no se han cargado estadísticas del edificio.',
        'No data is available for this supply type.': 'No hay datos disponibles para este tipo de suministro.',
        'Number of timestamps / values': 'Número de marcas de tiempo / valores',
        'Request building statistics': 'Solicitar estadísticas del edificio',
        'Requesting building statistics...': 'Solicitando estadísticas del edificio...',

        // Time Series & Last Values
        '#chapter-15': 'Time Series & Last Values',
        '{from} to {to}': '{from} hasta {to}',
        'Chart type': 'Tipo de gráfico',
        'Current cost value {key}': 'Valor de coste actual {key}',
        'Current value API request completed successfully. ({durationMs} ms)': 'La solicitud API del valor actual se completó correctamente. ({durationMs} ms)',
        'Current value output': 'Salida del valor actual',
        'Current value target': 'Destino del valor actual',
        'Grouped Bar Chart': 'Gráfico de barras agrupadas',
        'Interval value API request completed successfully. ({durationMs} ms)': 'Solicitud API de valores de intervalo completada correctamente. ({durationMs} ms)',
        'Interval value output': 'Salida de valores de intervalo',
        'Interval value target': 'Destino de valores de intervalo',
        'Interval values were loaded.': 'Se cargaron los valores de intervalo.',
        'Last value API request completed successfully. ({durationMs} ms)': 'La solicitud API del último valor se completó correctamente. ({durationMs} ms)',
        'Last value output': 'Salida del último valor',
        'Last value target': 'Destino del último valor',
        'Last value timestamp': 'Fecha del último valor',
        'Line Chart': 'Gráfico de líneas',
        'Loading time series chart...': 'Cargando gráfico de series temporales...',
        'No time series chart available.': 'No hay gráfico de series temporales disponible.',
        'No time series values were found for sensor {sensor}.': 'No se encontraron valores de series temporales para el sensor {sensor}.',
        'No valid time series chart data available.': 'No hay datos válidos de gráfico de series temporales disponibles.',
        'Parsed columns': 'Columnas analizadas',
        'Parsed rows': 'Filas analizadas',
        'Period': 'Período',
        'Power in kWh': 'Potencia en kWh',
        'Power in kW': 'Potencia en kW',
        'PV power in kW': 'Potencia FV en kW',
        'Power history (3 months)': 'Historial de potencia (3 meses)',
        'Power history (12 months)': 'Historial de potencia (12 meses)',
        'Power history {key}': 'Historial de potencia (valores de 15 min) {key}',
        'Heating power history (3 months)': 'Historial de potencia térmica (3 meses)',
        'Heating power history (12 months)': 'Historial de potencia térmica (12 meses)',
        'Heating power history {key}': 'Historial de potencia térmica {key}',
        'Heating power in kW': 'Potencia térmica en kW',
        'Preview rows shown': 'Filas de vista previa mostradas',
        'Request and inspect time series data.': 'Solicita e inspecciona datos de series temporales.',
        'Request Current Value': 'Solicitar valor actual',
        'Request Current Values': 'Solicitar valores actuales',
        'Request interval differences for one or more sensors.': 'Solicita diferencias de intervalo para uno o más sensores.',
        'Request Interval Values': 'Solicitar valores de intervalo',
        'Request Last Value': 'Solicitar último valor',
        'Request Last Values': 'Solicitar últimos valores',
        'Request the latest value for one or more sensors.': 'Solicita el último valor de uno o varios sensores.',
        'Request Time Series': 'Solicitar series temporales',
        'Requesting current value dataset...': 'Solicitando conjunto de datos del valor actual...',
        'Requesting current value...': 'Solicitando valor actual...',
        'Requesting current values...': 'Solicitando valores actuales...',
        'Requesting interval value dataset...': 'Solicitando conjunto de datos de valores de intervalo...',
        'Requesting interval values...': 'Solicitando valores de intervalo...',
        'Showing only the first {limit} parsed rows to keep browser rendering lightweight.': 'Solo se muestran las primeras {limit} filas analizadas para mantener ligera la visualización en el navegador.',
        'Stacked Bar Chart': 'Gráfico de barras apiladas',
        'The calculation code is not available for this calculated value.': 'No se ha definido ningún calcCode para este valor calculado.',
        'The current value request failed because the current API token appears to be expired. Renew the login and try again.': 'La solicitud del valor actual falló porque el token de API actual parece haber caducado. Renueva el inicio de sesión e inténtalo de nuevo.',
        'The displayed and classified value was calculated with "calc" as follows:': 'El valor mostrado y clasificado se calculó con "calc" de la siguiente manera:',
        'The displayed value was calculated with calc.': 'El valor mostrado se calculó con calc.',
        'The interval value request failed because the current API token appears to be expired. Renew the login and try again.': 'La solicitud de valores de intervalo falló porque el token de API actual parece haber caducado. Renueva el inicio de sesión e inténtalo de nuevo.',
        'The raw current value response will be shown here.': 'La respuesta bruta del valor actual se mostrará aquí.',
        'The raw interval value response will be shown here.': 'La respuesta bruta de los valores de intervalo se mostrará aquí.',
        'The raw last value response will be shown here.': 'La respuesta bruta del último valor se mostrará aquí.',
        'The raw time series response will be shown here.': 'La respuesta bruta de series temporales se mostrará aquí.',
        'Time interval': 'Intervalo de tiempo',
        'Time series API request completed successfully. ({durationMs} ms)': 'La solicitud API de series temporales se completó correctamente. ({durationMs} ms)',
        'Time series chart output': 'Salida del gráfico de series temporales',
        'Time series group': 'Grupo de series temporales',
        'Time series output': 'Salida de series temporales',
        'Time series range': 'Rango de series temporales',
        'Time series target': 'Destino de series temporales',
        'Time series values for sensor {sensor} are not numeric.': 'Los valores de series temporales del sensor {sensor} no son numéricos.',

        // Overview Results
        '#chapter-16': 'Overview Results',
        'No electricity consumption sensor was found for this system.': 'No se encontró ningún sensor de consumo eléctrico para este sistema.',
        'No sensors were found for the current selection.': 'No se encontraron sensores para la selección actual.',

        // System Statistics
        '#chapter-17': 'System Statistics',
        'Annual electricity, heat, water consumption and photovoltaic generation calculated from meter reading differences for system {key}.': 'Consumos anuales de electricidad, calor y agua, así como generación fotovoltaica, calculados a partir de diferencias de lecturas para el sistema {key}.',

        // KPI, Metrics & Business Labels
        '#chapter-18': 'KPI, Metrics & Business Labels',
        'Base load prediction': 'Predicción de carga base',
        'Base load prediction (14d)': 'Predicción de carga base (14d)',
        'Base load prediction (all)': 'Predicción de carga base (todo)',
        'Base load prediction {key}': 'Predicción de carga base {key}',
        'Browse all available KPI definitions and example sensor names.': 'Consulta todas las definiciones de KPI disponibles y los nombres de sensores de ejemplo.',
        'Cost last 14 days': 'Coste de los últimos 14 días',
        'Cost last 14 days {key}': 'Coste de los últimos 14 días {key}',
        'Cost last month': 'Coste del último mes',
        'Cost last value {key}': 'Último valor de coste {key}',
        'Cost last year': 'Coste del último año',
        'Cost overall': 'Coste total',
        'Cost overall {key}': 'Coste total {key}',
        'Comparison value from {date}': 'Valor de comparación del {date}',
        'Cutoff temperature': 'Temperatura de corte',
        'Cutoff temperature {key}': 'Temperatura de corte {key}',
        'Current cost value': 'Valor de coste actual',
        'Example KPI': 'KPI de ejemplo',
        'Heating energy prediction {key}': 'Predicción de energía térmica {key}',
        'Heating energy prediction': 'Predicción de energía térmica',
        'KPI value selection': 'Selección de valor KPI',
        'Measured PV value {key}': 'Valor FV medido {key}',
        'Measured PV values {key}': 'Valores FV medidos {key}',
        'Measured PV power {key}': 'Potencia FV medida {key}',
        'Measured PV power history (3 months)': 'Historial de potencia FV (3 meses)',
        'Measured PV power history (12 months)': 'Historial de potencia FV (12 meses)',
        'Predicted PV value {key} (new)': 'Valor FV previsto {key} (nuevo)',
        'Predicted PV value {key} (recent)': 'Valor FV previsto {key} (actual)',
        'Predicted PV values {key} (new)': 'Valores FV previstos {key} (nuevo)',
        'Predicted PV values {key} (recent)': 'Valores FV previstos {key} (actual)',
        'Ratio of Measurement to Prediction': 'Relación entre medición y predicción',
        'Saved energy {key}': 'Energía ahorrada {key}',
        'Saved energy overall': 'Energía ahorrada total',
        'Saved energy overall {key}': 'Energía ahorrada total {key}',
        'Saved energy last year': 'Energía ahorrada el último año',
        'Saved energy last year {key}': 'Energía ahorrada el último año {key}',
        'Saved energy last month': 'Energía ahorrada el último mes',
        'Saved energy last month {key}': 'Energía ahorrada el último mes {key}',
        'No historical comparison value is available.': 'No hay ningún valor histórico de comparación disponible.',
        'Show KPI value {index}': 'Mostrar valor KPI {index}',
        'Show heating energy prediction': 'Mostrar predicción de energía térmica',
        'Show sensor history': 'Mostrar historial del sensor',
        'Show sensor history for 3 months': 'Mostrar historial del sensor de tres meses',
        'Show sensor history for 12 months': 'Mostrar historial del sensor de doce meses',
        'Show sensor history for 13 months': 'Mostrar historial del sensor de trece meses',
        'Show measured PV power history for 3 months': 'Mostrar historial de potencia FV de tres meses',
        'Show measured PV power history for 12 months': 'Mostrar historial de potencia FV de doce meses',
        'Show power history for 3 months': 'Mostrar historial de potencia de tres meses',
        'Show power history for 12 months': 'Mostrar historial de potencia de doce meses',
        'Show heating power history for 3 months': 'Mostrar historial de potencia térmica de tres meses',
        'Show heating power history for 12 months': 'Mostrar historial de potencia térmica de doce meses',
        'Sensor history': 'Historial del sensor',
        'Sensor history (3 months)': 'Historial del sensor (3 meses)',
        'Sensor history (12 months)': 'Historial del sensor (12 meses)',
        'Sensor history (13 months)': 'Historial del sensor (13 meses)',
        'Sensor history {key}': 'Historial del sensor {key}',
        'Weekend reduction {key}': 'Reducción de fin de semana {key}',
        'Weekend reduction': 'Reducción de fin de semana',
        'Weekend reduction value {key}': 'Valor de reducción de fin de semana {key}',

        // Debug, Request History & Technical Details
        '#chapter-19': 'Debug, Request History & Technical Details',
        'API Calls': 'Llamadas API',
        'API Demos': 'Demos de API',
        'API Requests': 'Solicitudes API',
        'Beautify JSON': 'Formatear JSON',
        'Browse configured devices and their available sensors.': 'Consulta los dispositivos configurados y sus sensores disponibles.',
        'Convert to Plotly JSON': 'Convertir a JSON de Plotly',
        'Enter a Mermaid definition and render it with the shared StaticMermaidDiagram component.': 'Introduce una definición de Mermaid y procésala con el componente compartido StaticMermaidDiagram.',
        'Enter Markdown text and render it with the shared StaticMarkdown component.': 'Introduce texto Markdown y procésalo con el componente compartido StaticMarkdown.',
        'Enter Plotly JSON or a Chart.js configuration and render it with the shared StaticStoredPlotlyPlot component.': 'Introduce JSON de Plotly o una configuración de Chart.js y procésala con el componente compartido StaticStoredPlotlyPlot.',
        'Invalid chart configuration': 'Configuración de gráfico no válida',
        'Invalid Plotly JSON.': 'JSON de Plotly no válido.',
        'Mermaid': 'Mermaid',
        'Mermaid source': 'Código fuente Mermaid',
        'Markdown source': 'Código fuente Markdown',
        'Plotly JSON': 'JSON de Plotly',
        'Plotly or Chart.js JSON': 'JSON de Plotly o Chart.js',
        'Plotly JSON must describe an object.': 'El JSON de Plotly debe describir un objeto.',
        'Render': 'Renderizar',
        'Reference & Tools': 'Referencia y herramientas',
        'Renderer Demos': 'Demos de renderizado',
        'Clear error log': 'Borrar registro de errores',
        'Error Log': 'Registro de errores',
        'Error Diagnostics': 'Diagnóstico de errores',
        'Market & Prices': 'Mercado y precios',
        'No errors have been logged yet.': 'Aún no se han registrado errores.',
        'Occurred at': 'Ocurrido el',
        'Review application errors saved locally in this browser.': 'Revisa los errores de la aplicación guardados localmente en este navegador.',
        'Source': 'Origen',
        'Stores application errors locally to support troubleshooting without browser console output.': 'Guarda los errores de la aplicación localmente para facilitar la resolución de problemas sin la consola del navegador.',
        'Inspect API requests made during the current browser session.': 'Inspecciona las solicitudes API realizadas durante la sesión actual del navegador.',
        'n/a': 'n/d',
        'Original request': 'Solicitud original',
        'Overviews': 'Resúmenes',
        'Other parameters': 'Otros parámetros',
        'Parameters': 'Parámetros',
        'Raw response preview': 'Vista previa de respuesta bruta',
        'Request #{id}': 'Solicitud #{id}',
        'Select a debug tool to inspect the current application state:': 'Selecciona una herramienta de depuración para inspeccionar el estado actual de la aplicación:',

        // Contact & Company
        '#chapter-20': 'Contact & Company',
        'Address': 'Dirección',
        'Company': 'Empresa',
        'Country': 'País',
        'Elev {value}': 'Alt {value}',
        'Email': 'Correo electrónico',
        'Get in touch with EA Systems Dresden GmbH using the contact details below.': 'Póngase en contacto con EA Systems Dresden GmbH mediante los siguientes datos de contacto.',
        'Head office and primary contact': 'Sede principal y contacto principal',
        'Lat {value}': 'Lat {value}',
        'Lng {value}': 'Lng {value}',
        'Location': 'Ubicación',
        'Official company information and legal notices for EA Systems Dresden GmbH.': 'Información oficial de la empresa y avisos legales de EA Systems Dresden GmbH.',
        'Persons:': 'Personas:',
        'Phone': 'Teléfono',
        'Quick overview': 'Resumen rápido',
        'Source-aligned company information': 'Información de la empresa alineada con la fuente oficial',
        'Website': 'Sitio web',

        // Legal & Imprint
        '#chapter-21': 'Legal & Imprint',
        'Detailed legal information from the company imprint': 'Información legal detallada del aviso legal de la empresa',
        'Key registration and tax details': 'Datos clave de registro e impuestos',
        'Legal notices': 'Avisos legales',
        'Legal overview': 'Resumen legal',

        // Versions & Dependencies
        '#chapter-22': 'Versions & Dependencies',
        'Application version': 'Versión de la aplicación',
        'Baseline release information': 'Información básica de la versión',
        'City of Münster': 'Ciudad de Münster',
        'Core dependencies': 'Dependencias principales',
        'Current application version, selected dependency versions, and data licenses.': 'Versión actual de la aplicación, versiones de dependencias seleccionadas y licencias de datos.',
        'Current application version and selected dependency versions from package.json.': 'Versión actual de la aplicación y versiones seleccionadas de dependencias de package.json.',
        'Data licenses': 'Licencias de datos',
        'Data Licence Germany – Attribution – Version 2.0 (dl-de/by-2-0)': 'Licencia de Datos de Alemania – Atribución – Versión 2.0 (dl-de/by-2-0)',
        'Data source': 'Fuente de datos',
        'Dataset': 'Conjunto de datos',
        'Dependency': 'Dependencia',
        'Detailed release history can be added here later.': 'Aquí se puede añadir más adelante un historial detallado de versiones.',
        'Final energy consumption in the City of Münster': 'Consumo final de energía en la ciudad de Münster',
        'Rendered directly from the project CHANGELOG.md file.': 'Renderizado directamente desde el archivo CHANGELOG.md del proyecto.',
        'Selected package versions relevant to the current UI stack': 'Versiones de paquetes seleccionadas relevantes para la pila UI actual',
        'The data is processed within EnObserve and prepared for visualization and analysis.': 'Los datos se procesan dentro de EnObserve y se preparan para su visualización y análisis.',
        'This project uses open-data datasets. The respective data sources and license conditions are listed below.': 'Este proyecto utiliza conjuntos de datos abiertos. Las respectivas fuentes de datos y condiciones de licencia se enumeran a continuación.',
        'Updated at {date}': 'Actualizado el {date}',
        'Version history': 'Historial de versiones',

        // System Dashboard
        '#chapter-23': 'System Dashboard',
        '{count} configured sensors': '{count} sensores configurados',
        'Acknowledged': 'Confirmado',
        'Average water loss last 7 days': 'Pérdida media de agua últimos 7 días',
        'Average water loss last day': 'Pérdida media de agua del último día',
        'Active': 'Activo',
        'Active / Generation': 'Activo / Generacion',
        'Basic Consumption': 'Consumo básico',
        'Consumption': 'Consumo',
        'Data Outage': 'Fallo de datos',
        'Data type': 'Tipo de datos',
        'Electricity': 'Electricidad',
        'Energy': 'Energía',
        'Export PDF': 'Exportar PDF',
        'Grid Supply': 'Suministro de red',
        'Heat': 'Calor',
        'Link': 'Enlace',
        'Night Setback': 'Reducción nocturna',
        'Note: This is only a mockup.': 'Aviso: Esto es solo una maqueta.',
        'Peak Load': 'Carga máxima',
        'Performance Reduction': 'Pérdida de rendimiento',
        'Photovoltaics': 'Fotovoltaica',
        'Price Indicator': 'Indicador de precios',
        'Report': 'Informe',
        'Savings Potential': 'Potencial de ahorro',
        'Shutdown': 'Desconexión',
        'Simultaneous Heating and Cooling': 'Calefacción y refrigeración simultáneas',
        'Sporadic': 'esporádico',
        'System overview "{key}"': 'Resumen del sistema "{key}"',
        'System information': 'Información del sistema',
        'View sensor details': 'Ver detalles del sensor',
        'View system information': 'Mostrar información del sistema',
        'Water': 'Agua',
        'Water consumption {key}': 'Consumo de agua {key}',
        'Water flow rate {key}': 'Caudal de agua {key}',
        'Water loss': 'Pérdida de agua',
        'Water loss {key}': 'Pérdida de agua {key}',
        'Water loss overall': 'Pérdida de agua total',
        'Water loss last year': 'Pérdida de agua del último año',
        'Water loss last month': 'Pérdida de agua del último mes',
        'Water loss dashboard': 'Panel de pérdida de agua',
        'Water loss overview': 'Resumen de pérdida de agua',
        'Water volume {key}': 'Volumen de agua {key}',
        'Flow rate in m³/h': 'Caudal en m³/h',
        'Volume in m³': 'Volumen en m³',
        'Volume': 'Volumen',
        'Weekend Setback': 'Reducción de fin de semana',

        // Footer
        '#chapter-24': 'Footer',
        'Footer navigation': 'Navegación del pie de página',
    },
};
